import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import './Chat.css';
import { useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import api from '../../services/api';
import {
  getLatestSessions,
  getSessionMessages,
  SessionListItem
} from '../../services/sessionService';
import { getCurrentTokenUsage } from '../../services/tokenUsageService';
import { stripMarkdown } from '../../utils/stripMarkdown';
import { examOptions, gradeOptions } from '../../config';
import voiceConfig from '../../config/deepgram_aura_v1_config.json';
import ChatSidebar from './chat/components/ChatSidebar';
import ChatHeader from './chat/components/ChatHeader';
import ChatMessages from './chat/components/ChatMessages';
import ChatInput from './chat/components/ChatInput';
import ExamModeDialog from './chat/components/ExamModeDialog';
import MyTopicsModal from './chat/components/MyTopicsModal';
import ExamNotesPanel from './chat/components/ExamNotesPanel';
import { AssistantRenderMode, getMathStreamRenderState } from './chat/components/mathStreamBuffer';
import {
  ChatSession,
  ImagePreview,
  Message,
  PendingMedia,
  UploadMediaType
} from './chat/types';
import { chatPaginationLimit } from '../../config/chatPaginationConfiguration';
import { useProtectedRoute } from '../../hooks/useProtectedRoute';
import { useColorMode } from '../../context/ColorModeContext';
import zaptalkSplashAudio from '../../assets/audios/zaptalk_audio.mp3';
import {
  createTopicNote,
  deleteTopicNote,
  ExamTopicNote,
  ExamTopicSummary,
  fetchExamNoteCounts,
  fetchExamTopicsForSidebar,
  fetchTopicNotes,
  updateTopicNote,
} from '../../services/examNotesService';

type ChatRouteState = {
  sessionId?: string;
  chatMode?: string;
  subject?: string;
};

type ExamSidebarMode = 'topics' | 'notes';

type ExamNotesTreeNode = {
  nodeId: string;
  label: string;
  topicId: string;
  depth: number;
  type: 'topic' | 'subtopic';
  parentNodeId?: string;
};

type ZapTalkSupportOption = {
  topic: string;
};

const ZAPTALK_SUPPORT_OPTIONS: ZapTalkSupportOption[] = [
  { topic: 'Anxiety' },
  { topic: 'Motivation' },
  { topic: 'Confidence' },
  { topic: 'Sleep' },
  { topic: 'Depression' },
  { topic: 'Work Stress' },
  { topic: 'Relationships' },
  { topic: 'Exam Stress' },
  { topic: 'Pregnancy' },
  { topic: 'Loss' },
  { topic: 'LGBTQ+' },
  { topic: 'Low Energy' },
  { topic: 'Sex' },
];

const ZAPTALK_SESSION_SUBJECT = 'Mental Health';
const ZAPTALK_DEFAULT_TOPIC = 'General Support';
const ZAPTALK_SPLASH_SEEN_KEY = 'zaptalk_splash_seen';
const ZAPTALK_WELCOME_BACK_MESSAGE = 'Welcome back to ZapTalk. I am here to support you whenever you are ready.';
const EXAM_NOTE_MAX_LENGTH = 2000;
const MAX_PENDING_MEDIA = 8;
const IMAGE_MAX_DIMENSION = 1280;
const IMAGE_COMPRESSION_QUALITY = 0.45;

const buildSubtopicNodeId = (topicId: string, subtopicName: string, subtopicIndex: number): string => {
  const slug = subtopicName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return `${topicId}__subtopic__${subtopicIndex}__${slug || 'item'}`;
};

const normalizeExamLabel = (value: string): string => value.trim().toLowerCase();
const MONGO_OBJECT_ID_PATTERN = /^[a-f0-9]{24}$/i;
const isMongoObjectIdValue = (value: unknown): value is string =>
  typeof value === 'string' && MONGO_OBJECT_ID_PATTERN.test(value.trim());

const Chat: React.FC = () => {
  useProtectedRoute(); // Protect route from direct URL access when credits exhausted

  const location = useLocation();
  const { examId: examIdFromRoute } = useParams<{ examId?: string }>();
  const navigationType = useNavigationType();
  const chatRouteState = location.state as ChatRouteState | null;
  // const subject = location.state?.subject || 'General';
  const existingSessionId = chatRouteState?.sessionId || null;
  const MESSAGE_PAGE_SIZE = chatPaginationLimit;
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null);
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [selectedExamName, setSelectedExamName] = useState<string>('');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [examSidebarMode, setExamSidebarMode] = useState<ExamSidebarMode>('topics');
  const [examSidebarTopics, setExamSidebarTopics] = useState<ExamTopicSummary[]>([]);
  const [isLoadingExamTopics, setIsLoadingExamTopics] = useState(false);
  const [examTopicsError, setExamTopicsError] = useState<string | null>(null);
  const [selectedNotesTopicId, setSelectedNotesTopicId] = useState<string>('');
  const [examUnlockedThroughNodeId, setExamUnlockedThroughNodeId] = useState<string>('');
  const [notesByTopicId, setNotesByTopicId] = useState<Record<string, ExamTopicNote[]>>({});
  const [noteCountsByTopic, setNoteCountsByTopic] = useState<Record<string, number>>({});
  const [isLoadingSelectedTopicNotes, setIsLoadingSelectedTopicNotes] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [newNoteDraft, setNewNoteDraft] = useState('');
  const [newNoteError, setNewNoteError] = useState<string | null>(null);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteDraft, setEditingNoteDraft] = useState('');
  const [editingNoteError, setEditingNoteError] = useState<string | null>(null);
  const [updatingNoteId, setUpdatingNoteId] = useState<string | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const cachedNotesTopicsRef = useRef<Set<string>>(new Set());
  const lastHandledNotesQueryTopicRef = useRef<string>('');
  // const [examName, setExamName] = useState<string>('');
  const [incompleteTopic, setIncompleteTopic] = useState<string>('');
  const [incompleteSubtopic, setIncompleteSubtopic] = useState<string>('');
  const [isLoadingIncomplete, setIsLoadingIncomplete] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const { mode, toggleMode } = useColorMode();
  const isDarkMode = mode === 'dark';
  const [isExamModeOpen, setIsExamModeOpen] = useState(false);
  const [isExamMode, setIsExamMode] = useState(false);
  const [examDays, setExamDays] = useState(30);
  const [selectedExamType, setSelectedExamType] = useState('');
  const [expectedGrade, setExpectedGrade] = useState('');
  const [customExamType, setCustomExamType] = useState('');
  const [showCustomExamInput, setShowCustomExamInput] = useState(false);
  const [scoreType, setScoreType] = useState('percentage');
  const [scoreValue, setScoreValue] = useState('');
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const preventAutoScrollRef = useRef(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(existingSessionId);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sessionCreated = useRef(false);
  const clientSessionKey = useRef<string>('');
  const hasFetched = useRef(false);
  const [isTyping, setIsTyping] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [pendingMedia, setPendingMedia] = useState<PendingMedia[]>([]);
  const [isMediaProcessing, setIsMediaProcessing] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const isMounted = useRef(false);
  const requestInProgress = useRef(false);
  const examModeInitialized = useRef(false);
  const examAPITriggered = useRef(false); // Add guard to prevent duplicate API calls
  const [isResponding, setIsResponding] = useState(false);

  // Ref to track responding state synchronously to prevent multiple submissions
  const isRespondingRef = useRef(false);

  // Sync ref with state
  useEffect(() => {
    isRespondingRef.current = isResponding;
  }, [isResponding]);

  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [, setLastBotAudio] = useState<{ audioUrl: string; duration: number } | null>(null);
  const [isBotAudioManuallyPlaying, setIsBotAudioManuallyPlaying] = useState(false);
  const [isLastBotResponseComplete, setIsLastBotResponseComplete] = useState(false);
  const botAudioRef = useRef<HTMLAudioElement | null>(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedZapTalkTopic, setSelectedZapTalkTopic] = useState('');
  const [isCreditExhausted, setIsCreditExhausted] = useState(false);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
  const isZapTalkEntry = useMemo(
    () => chatRouteState?.chatMode === 'zaptalk' || chatRouteState?.subject === 'Mental Health',
    [chatRouteState],
  );
  const [showZapTalkSplash, setShowZapTalkSplash] = useState(isZapTalkEntry);
  const zaptalkSplashAudioRef = useRef<HTMLAudioElement | null>(null);
  const isZapTalkMode = useMemo(() => {
    if (isExamMode) {
      return false;
    }

    return isZapTalkEntry;
  }, [isZapTalkEntry, isExamMode]);
  const sessionSubject = useMemo(
    () => (isZapTalkMode ? ZAPTALK_SESSION_SUBJECT : selectedSubject),
    [isZapTalkMode, selectedSubject],
  );
  const zaptalkTopic = useMemo(
    () => selectedZapTalkTopic || ZAPTALK_DEFAULT_TOPIC,
    [selectedZapTalkTopic],
  );
  const isZapTalkTopicReady = useMemo(
    () => !isZapTalkMode || Boolean(selectedZapTalkTopic),
    [isZapTalkMode, selectedZapTalkTopic],
  );
  const llmGenerateEndpoint = useMemo(
    () => (isZapTalkMode ? '/api/llm/generate-zaptalk' : '/api/llm/generate'),
    [isZapTalkMode],
  );
  const [isZapTalkCategoryModalOpen, setIsZapTalkCategoryModalOpen] = useState(false);
  const [selectedZapTalkCategory, setSelectedZapTalkCategory] = useState('');
  const hasAutoOpenedZapTalkModalRef = useRef(false);

  const getRagImageUrlsFromResponse = (response: Response): string[] => {
    const headerValue =
      response.headers.get('X-Rag-Image-Urls') ||
      response.headers.get('x-rag-image-urls');
    if (!headerValue) {
      return [];
    }
    try {
      const parsed = JSON.parse(headerValue);
      console.log('Parsed X-Rag-Image-Urls:', parsed);
      return Array.isArray(parsed)
        ? parsed.filter(
          (url): url is string => typeof url === 'string' && url.trim().length > 0
        )
        : [];
    } catch (error) {
      console.error('Failed to parse X-Rag-Image-Urls header:', error);
      return [];
    }
  };

  const attachImagesToLatestAssistantMessage = (
    imageUrls: string[],
    source: string
  ) => {
    if (!imageUrls?.length || !isMounted.current) {
      return;
    }
    const timestamp = Date.now();
    const previews: ImagePreview[] = imageUrls.map((url, index) => ({
      file: new File([], `${source}-image-${timestamp}-${index}`, { type: 'image/png' }),
      preview: url,
      id: `${source}-image-${timestamp}-${index}`
    }));

    setMessages(messages => {
      if (!messages.length) {
        return messages;
      }

      const newMessages = [...messages];
      const lastIndex = newMessages.length - 1;
      const lastMessage = newMessages[lastIndex];

      if (lastMessage && !lastMessage.isUser) {
        const updatedLastMessage: Message = {
          ...lastMessage,
          images: [...(lastMessage.images || []), ...previews],
        };
        newMessages[lastIndex] = updatedLastMessage;
        return newMessages;
      }

      const imageOnlyMessage: Message = {
        role: 'assistant',
        content: '',
        isUser: false,
        timestamp: new Date(),
        images: previews,
      };
      return [...newMessages, imageOnlyMessage];
    });
  };

  // Attaches Wikipedia/Commons image URLs to the latest assistant message via a
  // dedicated field, so they render as small thumbnails (separate styling from
  // user-uploaded images).
  const attachTopicImagesToLatestAssistantMessage = (imageUrls: string[]) => {
    if (!imageUrls?.length || !isMounted.current) {
      return;
    }
    setMessages(messages => {
      if (!messages.length) {
        return messages;
      }
      const newMessages = [...messages];
      const lastIndex = newMessages.length - 1;
      const lastMessage = newMessages[lastIndex];
      if (lastMessage && !lastMessage.isUser) {
        newMessages[lastIndex] = {
          ...lastMessage,
          topicImages: [...(lastMessage.topicImages || []), ...imageUrls].slice(0, 2),
        };
        return newMessages;
      }
      return messages;
    });
  };

  // Flags the latest assistant message as waiting on (or done waiting on) its
  // topic images, so the bubble can show skeletons for exactly that window.
  const setTopicImagesLoadingOnLatestAssistantMessage = (loading: boolean) => {
    if (!isMounted.current) {
      return;
    }
    setMessages(messages => {
      if (!messages.length) {
        return messages;
      }
      const newMessages = [...messages];
      const lastIndex = newMessages.length - 1;
      const lastMessage = newMessages[lastIndex];
      if (lastMessage && !lastMessage.isUser) {
        newMessages[lastIndex] = { ...lastMessage, topicImagesLoading: loading };
        return newMessages;
      }
      return messages;
    });
  };

  // Whether a question looks like a real, look-up-able topic rather than a
  // greeting/filler ("hi", "ok", "thanks"). The backend also makes the finer
  // entity/NONE decision; this just avoids an obvious wasted call.
  const isImageWorthyQuery = (text: string): boolean => {
    const t = (text || '').trim().toLowerCase();
    if (t.length < 3) return false;
    if (!/[a-z]/.test(t)) return false;
    const normalized = t.replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    const fillers = new Set<string>([
      'hi', 'hii', 'hiii', 'hey', 'heyy', 'hello', 'helo', 'yo', 'sup', 'hola',
      'ok', 'oky', 'okay', 'okey', 'k', 'kk', 'fine', 'cool', 'nice', 'great',
      'good', 'awesome', 'perfect', 'done', 'thanks', 'thank', 'thankyou',
      'thank you', 'thx', 'tq', 'ty', 'welcome', 'yes', 'yeah', 'yep', 'yup',
      'no', 'nope', 'nah', 'hmm', 'hmmm', 'oh', 'ohh', 'wow', 'lol', 'haha',
      'bye', 'goodbye', 'see you', 'gm', 'gn',
      'good morning', 'good afternoon', 'good evening', 'good night',
    ]);
    if (fillers.has(normalized)) return false;
    const words = normalized.split(' ');
    if (words.length === 1 && fillers.has(words[0])) return false;
    return true;
  };

  // Fetches Wikipedia/Commons images for a question (async, non-blocking) and
  // attaches them under the latest assistant message via the existing image
  // display path. Never throws. Not used for ZapTalk.
  const fetchAndAttachTopicImages = async (
    question: string,
    source: string,
    subject: string,
    session?: string | null,
  ) => {
    const q = (question || '').trim();
    // Not image-worthy: never enters the loading state, so no skeleton appears.
    if (!isImageWorthyQuery(q)) return;
    setTopicImagesLoadingOnLatestAssistantMessage(true);
    try {
      // Sending the session lets the backend persist these images against the
      // question, so reopening the chat from history still shows them.
      const sid = (session || '').trim();
      const res = await api.post('/llm/topic-images', {
        question: q,
        subject,
        ...(sid ? { sessionId: sid } : {}),
      });
      const images: string[] = Array.isArray(res.data?.images)
        ? res.data.images
            .filter(
              (url: unknown): url is string =>
                typeof url === 'string' && url.trim().startsWith('http')
            )
            .slice(0, 2)
        : [];
      console.log('[topic-images]', source, JSON.stringify(q), '->', images.length, 'image(s)');
      attachTopicImagesToLatestAssistantMessage(images);
    } catch (error) {
      console.error('[topic-images] request failed:', error);
    } finally {
      // Always clear it: on an empty result or an error the strip must vanish
      // rather than shimmer forever.
      setTopicImagesLoadingOnLatestAssistantMessage(false);
    }
  };

  const setLatestAssistantMessage = useCallback(
    (content: string, renderMode: AssistantRenderMode = 'markdown', audio?: Message['audio']) => {
      setMessages(messages => {
        if (!messages.length) {
          return messages;
        }

        const newMessages = [...messages];
        const lastIndex = newMessages.length - 1;
        const lastMessage = newMessages[lastIndex];

        if (lastMessage && !lastMessage.isUser) {
          newMessages[lastIndex] = {
            ...lastMessage,
            content,
            renderMode,
            ...(audio ? { audio } : {}),
          };
        }

        return newMessages;
      });
    },
    [],
  );

  const applyBufferedAssistantContent = useCallback(
    (rawContent: string, options?: { final?: boolean; audio?: Message['audio'] }) => {
      const renderState = getMathStreamRenderState(rawContent, { final: options?.final });
      if (options?.final && renderState.issues.length > 0) {
        console.warn('Assistant message fell back to plain text due to invalid streamed math:', renderState.issues);
      }
      setLatestAssistantMessage(renderState.content, renderState.renderMode, options?.audio);
      return renderState;
    },
    [setLatestAssistantMessage],
  );

  const navigate = useNavigate();
  const isNotesModeActive = isExamMode && examSidebarMode === 'notes';

  const examNotesTreeNodes = useMemo<ExamNotesTreeNode[]>(() => {
    const sortedTopics = examSidebarTopics.slice().sort((a, b) => a.order - b.order);
    const nodes: ExamNotesTreeNode[] = [];

    sortedTopics.forEach((topic) => {
      nodes.push({
        nodeId: topic.topicId,
        label: topic.title,
        topicId: topic.topicId,
        depth: 0,
        type: 'topic',
      });

      (topic.subtopics || []).forEach((subtopicName, subtopicIndex) => {
        const normalizedSubtopic = typeof subtopicName === 'string' ? subtopicName.trim() : '';
        if (!normalizedSubtopic) {
          return;
        }

        nodes.push({
          nodeId: buildSubtopicNodeId(topic.topicId, normalizedSubtopic, subtopicIndex),
          label: normalizedSubtopic,
          topicId: topic.topicId,
          depth: 1,
          type: 'subtopic',
          parentNodeId: topic.topicId,
        });
      });
    });

    return nodes;
  }, [examSidebarTopics]);

  const selectedNotesTopicNode = useMemo(
    () => examNotesTreeNodes.find((node) => node.nodeId === selectedNotesTopicId) || null,
    [examNotesTreeNodes, selectedNotesTopicId],
  );

  const selectedTopicNotes = useMemo(
    () => notesByTopicId[selectedNotesTopicId] || [],
    [notesByTopicId, selectedNotesTopicId],
  );

  const selectedNotesPanelTitle = useMemo(() => {
    if (!selectedNotesTopicNode) {
      return 'Select a topic';
    }

    if (selectedNotesTopicNode.type === 'topic') {
      return selectedNotesTopicNode.label;
    }

    const parentNode = examNotesTreeNodes.find((node) => node.nodeId === selectedNotesTopicNode.parentNodeId);
    if (!parentNode) {
      return selectedNotesTopicNode.label;
    }

    return `${parentNode.label} > ${selectedNotesTopicNode.label}`;
  }, [selectedNotesTopicNode, examNotesTreeNodes]);

  const selectedExamTopicNodeId = useMemo(() => {
    if (!isExamMode || examSidebarTopics.length === 0) {
      return '';
    }

    const normalizedTopic = normalizeExamLabel(incompleteTopic || '');
    const normalizedSubtopic = normalizeExamLabel(incompleteSubtopic || '');

    const selectedTopicForChat =
      examSidebarTopics.find((topic) => normalizeExamLabel(topic.topicName || topic.title) === normalizedTopic) ||
      examSidebarTopics.find((topic) => normalizeExamLabel(topic.title) === normalizedTopic);

    if (!selectedTopicForChat) {
      return '';
    }

    if (normalizedSubtopic) {
      const matchingSubtopicIndex = (selectedTopicForChat.subtopics || []).findIndex(
        (subtopic) => normalizeExamLabel(subtopic || '') === normalizedSubtopic
      );

      if (matchingSubtopicIndex >= 0) {
        const matchedSubtopic = selectedTopicForChat.subtopics?.[matchingSubtopicIndex] || '';
        return buildSubtopicNodeId(selectedTopicForChat.topicId, matchedSubtopic, matchingSubtopicIndex);
      }
    }

    if (selectedTopicForChat.subtopics && selectedTopicForChat.subtopics.length > 0) {
      return buildSubtopicNodeId(
        selectedTopicForChat.topicId,
        selectedTopicForChat.subtopics[0],
        0
      );
    }

    return selectedTopicForChat.topicId;
  }, [isExamMode, examSidebarTopics, incompleteTopic, incompleteSubtopic]);

  const lockedExamNodeIds = useMemo<Record<string, boolean>>(() => {
    if (!isExamMode || examNotesTreeNodes.length === 0) {
      return {};
    }

    const boundaryNodeId = examUnlockedThroughNodeId || selectedExamTopicNodeId;
    if (!boundaryNodeId) {
      return {};
    }

    const boundaryIndex = examNotesTreeNodes.findIndex((node) => node.nodeId === boundaryNodeId);
    if (boundaryIndex < 0) {
      return {};
    }

    return examNotesTreeNodes.reduce<Record<string, boolean>>((acc, node, index) => {
      if (index > boundaryIndex) {
        acc[node.nodeId] = true;
      }
      return acc;
    }, {});
  }, [isExamMode, examNotesTreeNodes, examUnlockedThroughNodeId, selectedExamTopicNodeId]);

  const getApiErrorMessage = useCallback((error: unknown, fallback: string) => {
    const maybeError = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };

    return maybeError?.response?.data?.message || maybeError?.message || fallback;
  }, []);

  const sanitizeNoteContent = useCallback((value: string) => {
    return value.replace(/\r\n/g, '\n').replace(/<[^>]*>/g, '').trim();
  }, []);

  const validateNoteContent = useCallback(
    (value: string) => {
      const normalized = sanitizeNoteContent(value);
      if (!normalized) {
        return 'Note cannot be empty.';
      }

      if (normalized.length > EXAM_NOTE_MAX_LENGTH) {
        return `Note cannot exceed ${EXAM_NOTE_MAX_LENGTH} characters.`;
      }

      return null;
    },
    [sanitizeNoteContent],
  );

  const refreshCreditUsage = useCallback(async () => {
    try {
      const usage = await getCurrentTokenUsage();
      const exhausted = usage.isLimitExceeded;
      setIsCreditExhausted(exhausted);
      return exhausted;
    } catch (error) {
      console.error('Failed to fetch credit usage:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    void refreshCreditUsage();
  }, [refreshCreditUsage]);

  useEffect(() => {
    if (!isZapTalkEntry) {
      setShowZapTalkSplash(false);
      return;
    }

    const hasSeenSplash = sessionStorage.getItem(ZAPTALK_SPLASH_SEEN_KEY) === '1';
    if (navigationType === 'POP' && hasSeenSplash) {
      setShowZapTalkSplash(false);
      return;
    }

    setShowZapTalkSplash(true);
    sessionStorage.setItem(ZAPTALK_SPLASH_SEEN_KEY, '1');
    const splashTimer = window.setTimeout(() => {
      setShowZapTalkSplash(false);
    }, 3000);

    return () => {
      window.clearTimeout(splashTimer);
    };
  }, [isZapTalkEntry, location.key, navigationType]);

  useEffect(() => {
    if (!showZapTalkSplash || !isZapTalkEntry) {
      if (zaptalkSplashAudioRef.current) {
        zaptalkSplashAudioRef.current.pause();
        zaptalkSplashAudioRef.current.currentTime = 0;
      }
      return;
    }

    const splashAudio = new Audio(zaptalkSplashAudio);
    splashAudio.preload = 'auto';
    zaptalkSplashAudioRef.current = splashAudio;

    void splashAudio.play().catch((error) => {
      console.warn('ZapTalk splash audio could not autoplay:', error);
    });

    return () => {
      splashAudio.pause();
      splashAudio.currentTime = 0;
      if (zaptalkSplashAudioRef.current === splashAudio) {
        zaptalkSplashAudioRef.current = null;
      }
    };
  }, [isZapTalkEntry, showZapTalkSplash]);

  // Voice selection state
  const [selectedVoice, setSelectedVoice] = useState<string>(() => {
    const saved = localStorage.getItem('selectedVoice');
    return saved || 'aura-luna-en'; // Default to Luna (recommended for tutoring)
  });
  const [isVoiceMenuOpen, setIsVoiceMenuOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    const saved = localStorage.getItem('selectedLanguage');
    return saved || 'English';
  });
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const isVoiceSupported = selectedLanguage === 'English';

  const UI_TEXT = useMemo(() => ({
    English: {
      greeting: 'Hi',
      errorResponse: 'Error: Failed to get response. Please try again.',
      errorLoad: 'Error loading session messages. Please try again.',
      stopped: 'Stopped...',
      welcome: 'Welcome back! I\'m ready to continue helping you with your studies.',
      welcomeWithSubtopic: (subtopic: string) =>
        `Welcome back! I'm ready to continue helping you with your studies on ${subtopic}.`,
      defineAbout: (topic: string) => `Define about ${topic}`,
    },
    Hindi: {
      greeting: 'नमस्ते',
      errorResponse: 'त्रुटि: जवाब नहीं मिला। कृपया फिर से कोशिश करें।',
      errorLoad: 'त्रुटि: सत्र संदेश लोड नहीं हो सके। कृपया फिर से कोशिश करें।',
      stopped: 'रोक दिया गया।',
      welcome: 'वापसी पर स्वागत है! मैं आपकी पढ़ाई में मदद करने के लिए तैयार हूँ।',
      welcomeWithSubtopic: (subtopic: string) =>
        `वापसी पर स्वागत है! मैं ${subtopic} पर आपकी पढ़ाई में मदद करने के लिए तैयार हूँ।`,
      defineAbout: (topic: string) => `${topic} के बारे में परिभाषित करें`,
    },
    Bengali: {
      greeting: 'নমস্কার',
      errorResponse: 'ত্রুটি: উত্তর পাওয়া যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।',
      errorLoad: 'ত্রুটি: সেশন বার্তা লোড করা যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।',
      stopped: 'বন্ধ করা হয়েছে।',
      welcome: 'ফিরে আসার জন্য স্বাগতম! আমি আপনার পড়াশোনায় সাহায্য করতে প্রস্তুত।',
      welcomeWithSubtopic: (subtopic: string) =>
        `ফিরে আসার জন্য স্বাগতম! আমি ${subtopic} বিষয়ে আপনার পড়াশোনায় সাহায্য করতে প্রস্তুত।`,
      defineAbout: (topic: string) => `${topic} সম্পর্কে সংজ্ঞা দিন`,
    },
    Telugu: {
      greeting: 'నమస్తే',
      errorResponse: 'లోపం: సమాధానం రాలేదు. దయచేసి మళ్లీ ప్రయత్నించండి.',
      errorLoad: 'లోపం: సెషన్ సందేశాలు లోడ్ కాలేదు. దయచేసి మళ్లీ ప్రయత్నించండి.',
      stopped: 'ఆపివేయబడింది.',
      welcome: 'తిరిగి స్వాగతం! మీ చదువులో సహాయం చేయడానికి నేను సిద్ధంగా ఉన్నాను.',
      welcomeWithSubtopic: (subtopic: string) =>
        `తిరిగి స్వాగతం! ${subtopic} పై మీ చదువులో సహాయం చేయడానికి నేను సిద్ధంగా ఉన్నాను.`,
      defineAbout: (topic: string) => `${topic} గురించి నిర్వచించండి`,
    },
    Marathi: {
      greeting: 'नमस्कार',
      errorResponse: 'त्रुटी: उत्तर मिळाले नाही. कृपया पुन्हा प्रयत्न करा.',
      errorLoad: 'त्रुटी: सत्र संदेश लोड झाले नाहीत. कृपया पुन्हा प्रयत्न करा.',
      stopped: 'थांबवले.',
      welcome: 'पुन्हा स्वागत आहे! तुमच्या अभ्यासात मदत करण्यासाठी मी तयार आहे.',
      welcomeWithSubtopic: (subtopic: string) =>
        `पुन्हा स्वागत आहे! ${subtopic} वर तुमच्या अभ्यासात मदत करण्यासाठी मी तयार आहे.`,
      defineAbout: (topic: string) => `${topic} बद्दल परिभाषित करा`,
    },
    Tamil: {
      greeting: 'வணக்கம்',
      errorResponse: 'பிழை: பதில் கிடைக்கவில்லை. தயவுசெய்து மீண்டும் முயற்சிக்கவும்.',
      errorLoad: 'பிழை: அமர்வு செய்திகளை ஏற்ற முடியவில்லை. தயவுசெய்து மீண்டும் முயற்சிக்கவும்.',
      stopped: 'நிறுத்தப்பட்டது.',
      welcome: 'மீண்டும் வரவேற்கிறோம்! உங்கள் படிப்பில் உதவ நான் தயாராக இருக்கிறேன்.',
      welcomeWithSubtopic: (subtopic: string) =>
        `மீண்டும் வரவேற்கிறோம்! ${subtopic} குறித்து உங்கள் படிப்பில் உதவ நான் தயாராக இருக்கிறேன்.`,
      defineAbout: (topic: string) => `${topic} பற்றி வரையறு`,
    },
    Gujarati: {
      greeting: 'નમસ્તે',
      errorResponse: 'ભૂલ: જવાબ મળ્યો નથી. કૃપા કરીને ફરી પ્રયાસ કરો.',
      errorLoad: 'ભૂલ: સેશન સંદેશા લોડ થઈ શક્યા નથી. કૃપા કરીને ફરી પ્રયાસ કરો.',
      stopped: 'બંધ કરવામાં આવ્યું.',
      welcome: 'પાછા આવવા બદલ સ્વાગત છે! તમારા અભ્યાસમાં મદદ કરવા હું તૈયાર છું.',
      welcomeWithSubtopic: (subtopic: string) =>
        `પાછા આવવા બદલ સ્વાગત છે! ${subtopic} વિષે તમારા અભ્યાસમાં મદદ કરવા હું તૈયાર છું.`,
      defineAbout: (topic: string) => `${topic} વિશે વ્યાખ્યા આપો`,
    },
    Kannada: {
      greeting: 'ನಮಸ್ಕಾರ',
      errorResponse: 'ದೋಷ: ಉತ್ತರ ಸಿಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
      errorLoad: 'ದೋಷ: ಸೆಷನ್ ಸಂದೇಶಗಳನ್ನು ಲೋಡ್ ಮಾಡಲಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
      stopped: 'ನಿಲ್ಲಿಸಲಾಗಿದೆ.',
      welcome: 'ಮತ್ತೆ ಸ್ವಾಗತ! ನಿಮ್ಮ ಓದಿನಲ್ಲಿ ಸಹಾಯ ಮಾಡಲು ನಾನು ಸಿದ್ಧನಿದ್ದೇನೆ.',
      welcomeWithSubtopic: (subtopic: string) =>
        `ಮತ್ತೆ ಸ್ವಾಗತ! ${subtopic} ಬಗ್ಗೆ ನಿಮ್ಮ ಓದಿನಲ್ಲಿ ಸಹಾಯ ಮಾಡಲು ನಾನು ಸಿದ್ಧನಿದ್ದೇನೆ.`,
      defineAbout: (topic: string) => `${topic} ಕುರಿತು ವ್ಯಾಖ್ಯಾನಿಸಿ`,
    },
    Malayalam: {
      greeting: 'നമസ്കാരം',
      errorResponse: 'പിശക്: മറുപടി ലഭിച്ചില്ല. ദയവായി വീണ്ടും ശ്രമിക്കുക.',
      errorLoad: 'പിശക്: സെഷൻ സന്ദേശങ്ങൾ ലോഡ് ചെയ്യാനായില്ല. ദയവായി വീണ്ടും ശ്രമിക്കുക.',
      stopped: 'നിർത്തി.',
      welcome: 'തിരികെ സ്വാഗതം! നിങ്ങളുടെ പഠനത്തിൽ സഹായിക്കാൻ ഞാൻ തയ്യാറാണ്.',
      welcomeWithSubtopic: (subtopic: string) =>
        `തിരികെ സ്വാഗതം! ${subtopic} സംബന്ധിച്ച് നിങ്ങളുടെ പഠനത്തിൽ സഹായിക്കാൻ ഞാൻ തയ്യാറാണ്.`,
      defineAbout: (topic: string) => `${topic}യെ നിർവചിക്കുക`,
    },
    Punjabi: {
      greeting: 'ਸਤ ਸ੍ਰੀ ਅਕਾਲ',
      errorResponse: 'ਗਲਤੀ: ਜਵਾਬ ਨਹੀਂ ਮਿਲਿਆ। ਕਿਰਪਾ ਕਰਕੇ ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
      errorLoad: 'ਗਲਤੀ: ਸੈਸ਼ਨ ਸੁਨੇਹੇ ਲੋਡ ਨਹੀਂ ਹੋ ਸਕੇ। ਕਿਰਪਾ ਕਰਕੇ ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
      stopped: 'ਰੋਕ ਦਿੱਤਾ ਗਿਆ।',
      welcome: 'ਵਾਪਸੀ \'ਤੇ ਸਵਾਗਤ ਹੈ! ਮੈਂ ਤੁਹਾਡੇ ਅਧਿਐਨ ਵਿੱਚ ਮਦਦ ਕਰਨ ਲਈ ਤਿਆਰ ਹਾਂ।',
      welcomeWithSubtopic: (subtopic: string) =>
        `ਵਾਪਸੀ \'ਤੇ ਸਵਾਗਤ ਹੈ! ${subtopic} ਬਾਰੇ ਤੁਹਾਡੇ ਅਧਿਐਨ ਵਿੱਚ ਮਦਦ ਕਰਨ ਲਈ ਤਿਆਰ ਹਾਂ।`,
      defineAbout: (topic: string) => `${topic} ਬਾਰੇ ਪਰਿਭਾਸ਼ਿਤ ਕਰੋ`,
    },
    Odia: {
      greeting: 'ନମସ୍କାର',
      errorResponse: 'ତ୍ରୁଟି: ଉତ୍ତର ମିଳିଲା ନାହିଁ। ଦୟାକରି ପୁନର୍ବାର ଚେଷ୍ଟା କରନ୍ତୁ।',
      errorLoad: 'ତ୍ରୁଟି: ସେସନ୍ ସନ୍ଦେଶ ଲୋଡ୍ ହୋଇନଥିଲା। ଦୟାକରି ପୁନର୍ବାର ଚେଷ୍ଟା କରନ୍ତୁ।',
      stopped: 'ବନ୍ଦ କରାଯାଇଛି।',
      welcome: 'ପୁନଃ ସ୍ୱାଗତ! ଆପଣଙ୍କ ପଢ଼ାଇରେ ସହାୟତା କରିବାକୁ ମୁଁ ପ୍ରସ୍ତୁତ।',
      welcomeWithSubtopic: (subtopic: string) =>
        `ପୁନଃ ସ୍ୱାଗତ! ${subtopic} ବିଷୟରେ ଆପଣଙ୍କ ପଢ଼ାଇରେ ସହାୟତା କରିବାକୁ ମୁଁ ପ୍ରସ୍ତୁତ।`,
      defineAbout: (topic: string) => `${topic} ବିଷୟରେ ପରିଭାଷା ଦିଅନ୍ତୁ`,
    },
    Sanskrit: {
      greeting: 'नमो नमः',
      errorResponse: 'दोषः: उत्तरं न प्राप्तम्। कृपया पुनः प्रयतताम्।',
      errorLoad: 'दोषः: सत्रसन्देशाः न प्राप्ताः। कृपया पुनः प्रयतताम्।',
      stopped: 'स्थगितम्।',
      welcome: 'पुनः स्वागतम्! भवतः अध्ययने साहाय्यं कर्तुं सज्जः अस्मि।',
      welcomeWithSubtopic: (subtopic: string) =>
        `पुनः स्वागतम्! ${subtopic} विषये भवतः अध्ययने साहाय्यं कर्तुं सज्जः अस्मि।`,
      defineAbout: (topic: string) => `${topic} विषये परिभाषां ददातु`,
    },
  }), []);

  const uiText = UI_TEXT[selectedLanguage as keyof typeof UI_TEXT] || UI_TEXT.English;
  const initialGreeting = uiText.greeting;
  const isAutoGreetingMessage = (message: string) => {
    const trimmed = message.trim();
    return Object.values(UI_TEXT).some(entry => entry.greeting === trimmed);
  };

  const resetMessagesState = () => {
    setMessages([]);
    setHasMoreMessages(false);
    setMessagesCursor(null);
    setIsLoadingOlderMessages(false);
  };

  const hideFirstUserMessage = (items: Message[]): Message[] => {
    const firstUserIndex = items.findIndex((message) => message.isUser);
    if (firstUserIndex < 0) {
      return items;
    }

    return items.filter((_, index) => index !== firstUserIndex);
  };

  const convertSessionMessages = (sessionMessages: any[]): Message[] => {
    // Topic images are persisted on the USER message — that is the question
    // they were resolved for — but they render under the assistant's answer,
    // so carry them forward to the next assistant message.
    let pendingTopicImages: string[] | undefined;

    return sessionMessages.reduce<Message[]>((acc, msg: any, index: number) => {
      // Hide only auto-greeting user prompts in ZapTalk to avoid confusing duplicated "Hi" entries.
      if (
        isZapTalkMode &&
        msg.role === 'user' &&
        typeof msg.content === 'string' &&
        isAutoGreetingMessage(msg.content) &&
        (!Array.isArray(msg.imageUrls) || msg.imageUrls.length === 0) &&
        !msg.audioUrl
      ) {
        return acc;
      }

      if (msg.role === 'user') {
        const urls: string[] = Array.isArray(msg.topicImageUrls)
          ? msg.topicImageUrls
              .filter(
                (url: unknown): url is string =>
                  typeof url === 'string' && url.trim().startsWith('http')
              )
              .slice(0, 2)
          : [];
        pendingTopicImages = urls.length ? urls : undefined;
      }

      let images: ImagePreview[] | undefined;
      if (msg.imageUrls && Array.isArray(msg.imageUrls) && msg.imageUrls.length > 0) {
        images = msg.imageUrls.map((url: string, imgIndex: number) => ({
          file: new File([], `image-${index}-${imgIndex}`, { type: 'image/png' }),
          preview: url,
          id: `session-img-${msg._id || 'no-id'}-${imgIndex}`,
        }));
      }

      let audio: { audioUrl: string; duration: number } | undefined;
      if (msg.audioUrl && typeof msg.audioUrl === 'string') {
        audio = {
          audioUrl: msg.audioUrl,
          duration: msg.audioDuration || 0,
        };
      }

      let topicImages: string[] | undefined;
      if (msg.role === 'assistant') {
        topicImages = pendingTopicImages;
        pendingTopicImages = undefined;
      }

      acc.push({
        role: msg.role,
        content: msg.content || '',
        isUser: msg.role === 'user',
        timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
        images,
        audio,
        topicImages,
      });

      return acc;
    }, []);
  };

  // AbortController for all fetches
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort previous
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  // Latest sessions state
  const [latestSessions, setLatestSessions] = useState<SessionListItem[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [sessionsPerPage] = useState(10);

  useEffect(() => {
    if (!examIdFromRoute) {
      return;
    }

    setIsExamMode(true);
    setSelectedDocumentId(examIdFromRoute);
  }, [examIdFromRoute]);

  // Handle location.state values when navigating from other components
  useEffect(() => {
    if (location.state) {
      console.log('setting all the states using location..');

      const {
        isExamMode,
        courseId,
        subject,
        topic,
        examName,
        incompleteTopic,
        incompleteSubtopic,
        chatMode,
      } = location.state as any;
      console.log(isExamMode, courseId, subject, examName, incompleteTopic, incompleteSubtopic);
      const incomingZapTalkMode = chatMode === 'zaptalk' || subject === 'Mental Health';
      if (incomingZapTalkMode) {
        setSelectedSubject(ZAPTALK_SESSION_SUBJECT);
      }
      // Set isExamMode if provided
      if (isExamMode !== undefined) {
        setIsExamMode(isExamMode);
      }

      // Set courseId 
      if (courseId) {
        setSelectedDocumentId(courseId);
      }

      // Set subject if provided
      if (!incomingZapTalkMode && subject) {
        setSelectedSubject(subject);
      }

      if (incomingZapTalkMode && topic) {
        setSelectedZapTalkTopic(String(topic));
      }

      // Set examName if provided
      if (examName) {
        setSelectedExamName(examName);
      }

      // Set incomplete topic/subtopic
      if (incompleteTopic) {
        setIncompleteTopic(incompleteTopic);
      }

      if (incompleteSubtopic) {
        setIncompleteSubtopic(incompleteSubtopic);
      }

      console.log("State Details:", location.state);

      // Reset initialization flag when exam mode parameters change
      examModeInitialized.current = false;
    } else if (!examIdFromRoute && !isExamMode) {
      // If no location.state, set a default subject for normal mode
      console.log('No location.state provided, setting default subject');
      setSelectedSubject((previousSubject) => {
        if (previousSubject === ZAPTALK_SESSION_SUBJECT) {
          return 'General';
        }
        return previousSubject || 'General';
      });
      console.log('Set default subject: General');
    }
  }, [location.state, examIdFromRoute, isExamMode]);

  useEffect(() => {
    if (isZapTalkMode && !hasAutoOpenedZapTalkModalRef.current) {
      hasAutoOpenedZapTalkModalRef.current = true;
      setIsZapTalkCategoryModalOpen(true);

      if (selectedZapTalkTopic) {
        const matchedOption = ZAPTALK_SUPPORT_OPTIONS.find(
          (option) => option.topic.toLowerCase() === selectedZapTalkTopic.toLowerCase(),
        );
        setSelectedZapTalkCategory(matchedOption?.topic || '');
      } else {
        setSelectedZapTalkCategory('');
      }
      return;
    }

    if (!isZapTalkMode) {
      hasAutoOpenedZapTalkModalRef.current = false;
      setIsZapTalkCategoryModalOpen(false);
      setSelectedZapTalkCategory('');
      setSelectedZapTalkTopic('');
    }
  }, [isZapTalkMode, selectedZapTalkTopic]);


  // Update selectedDocumentId when courseId is provided from navigation
  useEffect(() => {
    if (isExamMode && selectedDocumentId) {
      console.log('🎯 Setting selectedDocumentId from navigation state:', selectedDocumentId);
      setSelectedDocumentId(selectedDocumentId);
      // Reset initialization flag when exam mode parameters change
      examModeInitialized.current = false;
    }
  }, [isExamMode, selectedDocumentId]);
  // Date filter state
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });
  const [filteredSessions, setFilteredSessions] = useState<SessionListItem[]>([]);

  // Exam mode submit and result state
  const [isExamSubmitting, setIsExamSubmitting] = useState(false);
  const [examSubmitError, setExamSubmitError] = useState<string | null>(null);
  const [isExamResultOpen, setIsExamResultOpen] = useState(false);
  const [examResult, setExamResult] = useState<any>(null);
  const [examResultTitle, setExamResultTitle] = useState<string>('Generated Exam Topics');

  // State for tracking completed topics and subtopics
  const [completedTopics, setCompletedTopics] = useState<Record<string, boolean>>({});
  const [completedSubtopics, setCompletedSubtopics] = useState<Record<string, Record<string, boolean>>>({});

  const queryPanel = useMemo(() => {
    const panel = new URLSearchParams(location.search).get('panel');
    if (panel === 'notes' || panel === 'topics') {
      return panel;
    }
    return null;
  }, [location.search]);

  const queryTopicId = useMemo(() => {
    const topic = new URLSearchParams(location.search).get('topic');
    return topic ? topic.trim() : '';
  }, [location.search]);

  const loadExamTopicsForSidebar = useCallback(async () => {
    if (!selectedDocumentId) {
      setExamSidebarTopics([]);
      return;
    }

    if (!isMongoObjectIdValue(selectedDocumentId)) {
      setExamSidebarTopics([]);
      setExamTopicsError('Exam ID is not ready yet. Please reopen this exam from My Exams.');
      return;
    }

    try {
      setIsLoadingExamTopics(true);
      setExamTopicsError(null);

      const examData = await fetchExamTopicsForSidebar(selectedDocumentId);
      const topics = Array.isArray(examData?.topics)
        ? examData.topics.slice().sort((a, b) => a.order - b.order)
        : [];

      setExamSidebarTopics(topics);
      setSelectedExamName((previous) => previous || examData?.examName || '');
      setSelectedSubject((previous) => previous || examData?.subject || 'General');

      if (topics.length > 0) {
        setIncompleteTopic((previous) => previous || topics[0].topicName || topics[0].title);
        setIncompleteSubtopic((previous) => previous || topics[0].subtopics?.[0] || '');
      }
    } catch (error) {
      setExamTopicsError(getApiErrorMessage(error, 'Failed to load exam topics.'));
    } finally {
      setIsLoadingExamTopics(false);
    }
  }, [selectedDocumentId, getApiErrorMessage]);

  useEffect(() => {
    if (!isExamMode || !selectedDocumentId) {
      setExamSidebarTopics([]);
      setExamTopicsError(null);
      setIsLoadingExamTopics(false);
      return;
    }

    void loadExamTopicsForSidebar();
  }, [isExamMode, selectedDocumentId, loadExamTopicsForSidebar]);

  useEffect(() => {
    cachedNotesTopicsRef.current.clear();
    lastHandledNotesQueryTopicRef.current = '';
    setExamUnlockedThroughNodeId('');
    setSelectedNotesTopicId('');
    setNotesByTopicId({});
    setNoteCountsByTopic({});
    setNewNoteDraft('');
    setNewNoteError(null);
    setNotesError(null);
    setEditingNoteId(null);
    setEditingNoteDraft('');
    setEditingNoteError(null);
  }, [selectedDocumentId]);

  useEffect(() => {
    if (!isExamMode || !selectedExamTopicNodeId || examNotesTreeNodes.length === 0) {
      return;
    }

    const selectedIndex = examNotesTreeNodes.findIndex((node) => node.nodeId === selectedExamTopicNodeId);
    if (selectedIndex < 0) {
      return;
    }

    const currentUnlockedIndex = examNotesTreeNodes.findIndex((node) => node.nodeId === examUnlockedThroughNodeId);
    if (currentUnlockedIndex < selectedIndex) {
      setExamUnlockedThroughNodeId(selectedExamTopicNodeId);
    }
  }, [isExamMode, selectedExamTopicNodeId, examNotesTreeNodes, examUnlockedThroughNodeId]);

  useEffect(() => {
    if (!isExamMode) {
      return;
    }

    if (queryPanel) {
      setExamSidebarMode(queryPanel);
    }
  }, [isExamMode, queryPanel]);

  useEffect(() => {
    if (!isExamMode || examNotesTreeNodes.length === 0) {
      if (examNotesTreeNodes.length === 0) {
        setSelectedNotesTopicId('');
      }
      return;
    }

    const getNodeById = (nodeId: string) => examNotesTreeNodes.find((node) => node.nodeId === nodeId);
    const resolveSubtopicNodeId = (nodeId: string): string => {
      if (!nodeId) {
        return '';
      }

      const matchedNode = getNodeById(nodeId);
      if (!matchedNode) {
        return '';
      }

      if (matchedNode.type === 'subtopic') {
        return matchedNode.nodeId;
      }

      const firstSubtopicNode = examNotesTreeNodes.find(
        (node) => node.type === 'subtopic' && node.parentNodeId === matchedNode.nodeId
      );
      return firstSubtopicNode?.nodeId || matchedNode.nodeId;
    };

    const nodeFromQuery = resolveSubtopicNodeId(queryTopicId);
    const hasQueryChanged = queryTopicId !== lastHandledNotesQueryTopicRef.current;
    if (hasQueryChanged) {
      lastHandledNotesQueryTopicRef.current = queryTopicId;
      if (nodeFromQuery && nodeFromQuery !== selectedNotesTopicId) {
        setSelectedNotesTopicId(nodeFromQuery);
        return;
      }
    }

    const currentNode = resolveSubtopicNodeId(selectedNotesTopicId);
    if (currentNode && currentNode !== selectedNotesTopicId) {
      setSelectedNotesTopicId(currentNode);
      return;
    }

    if (currentNode) {
      return;
    }

    const defaultNodeId =
      examNotesTreeNodes.find((node) => node.type === 'subtopic')?.nodeId ||
      examNotesTreeNodes.find((node) => node.type === 'topic')?.nodeId ||
      examNotesTreeNodes[0].nodeId;
    setSelectedNotesTopicId(nodeFromQuery || defaultNodeId);
  }, [isExamMode, examNotesTreeNodes, selectedNotesTopicId, queryTopicId]);

  useEffect(() => {
    if (!isExamMode || !selectedDocumentId) {
      return;
    }

    const params = new URLSearchParams();
    params.set('panel', examSidebarMode);
    if (examSidebarMode === 'notes' && selectedNotesTopicId) {
      params.set('topic', selectedNotesTopicId);
    }

    const targetPath = `/exam/${selectedDocumentId}`;
    const targetSearch = `?${params.toString()}`;

    if (location.pathname !== targetPath || location.search !== targetSearch) {
      navigate(`${targetPath}${targetSearch}`, { replace: true });
    }
  }, [
    isExamMode,
    selectedDocumentId,
    examSidebarMode,
    selectedNotesTopicId,
    location.pathname,
    location.search,
    navigate,
  ]);

  const loadExamNoteCounts = useCallback(async () => {
    if (!selectedDocumentId) {
      return;
    }

    try {
      const counts = await fetchExamNoteCounts(selectedDocumentId);
      setNoteCountsByTopic((previous) => ({ ...previous, ...counts }));
    } catch (error) {
      console.error('Failed to load note counts:', error);
    }
  }, [selectedDocumentId]);

  useEffect(() => {
    if (!isNotesModeActive || !selectedDocumentId) {
      return;
    }

    void loadExamNoteCounts();
  }, [isNotesModeActive, selectedDocumentId, loadExamNoteCounts]);

  const loadNotesForTopic = useCallback(async (topicId: string, forceRefresh = false) => {
    if (!selectedDocumentId || !topicId) {
      return;
    }

    if (!forceRefresh && cachedNotesTopicsRef.current.has(topicId)) {
      return;
    }

    try {
      setIsLoadingSelectedTopicNotes(true);
      setNotesError(null);
      const notes = await fetchTopicNotes(selectedDocumentId, topicId);

      setNotesByTopicId((previous) => ({
        ...previous,
        [topicId]: notes,
      }));

      setNoteCountsByTopic((previous) => ({
        ...previous,
        [topicId]: notes.length,
      }));

      cachedNotesTopicsRef.current.add(topicId);
    } catch (error) {
      setNotesError(getApiErrorMessage(error, 'Failed to load notes.'));
    } finally {
      setIsLoadingSelectedTopicNotes(false);
    }
  }, [selectedDocumentId, getApiErrorMessage]);

  useEffect(() => {
    if (!isNotesModeActive || !selectedNotesTopicId) {
      return;
    }

    void loadNotesForTopic(selectedNotesTopicId);
  }, [isNotesModeActive, selectedNotesTopicId, loadNotesForTopic]);

  useEffect(() => {
    setNewNoteDraft('');
    setNewNoteError(null);
    setNotesError(null);
    setEditingNoteId(null);
    setEditingNoteDraft('');
    setEditingNoteError(null);
  }, [selectedNotesTopicId]);

  useEffect(() => {
    if (!isExamMode || !selectedExamTopicNodeId) {
      return;
    }

    if (!selectedNotesTopicId) {
      setSelectedNotesTopicId(selectedExamTopicNodeId);
      return;
    }

    if (lockedExamNodeIds[selectedNotesTopicId]) {
      setSelectedNotesTopicId(selectedExamTopicNodeId);
    }
  }, [isExamMode, selectedExamTopicNodeId, selectedNotesTopicId, lockedExamNodeIds]);

  const handleExamSidebarModeChange = useCallback((mode: ExamSidebarMode) => {
    setExamSidebarMode(mode);
  }, []);

  const handleExamTopicSelect = useCallback((nodeId: string) => {
    if (lockedExamNodeIds[nodeId]) {
      return;
    }

    const selectedNode = examNotesTreeNodes.find((node) => node.nodeId === nodeId);
    if (!selectedNode) {
      return;
    }

    const selectedTopicForChat = examSidebarTopics.find((topic) => topic.topicId === selectedNode.topicId);
    if (!selectedTopicForChat) {
      return;
    }

    setIncompleteTopic(selectedTopicForChat.topicName || selectedTopicForChat.title);

    if (selectedNode.type === 'subtopic') {
      setIncompleteSubtopic(selectedNode.label);
    } else {
      setIncompleteSubtopic(selectedTopicForChat.subtopics?.[0] || '');
    }

    if (window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [lockedExamNodeIds, examNotesTreeNodes, examSidebarTopics]);

  const handleNotesTopicSelect = useCallback((nodeId: string) => {
    if (lockedExamNodeIds[nodeId]) {
      return;
    }

    const selectedNode = examNotesTreeNodes.find((node) => node.nodeId === nodeId);
    if (!selectedNode) {
      return;
    }

    if (selectedNode.type === 'topic') {
      const firstSubtopicNode = examNotesTreeNodes.find(
        (node) => node.type === 'subtopic' && node.parentNodeId === selectedNode.nodeId
      );
      setSelectedNotesTopicId(firstSubtopicNode?.nodeId || selectedNode.nodeId);
    } else {
      setSelectedNotesTopicId(nodeId);
    }

    if (window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [lockedExamNodeIds, examNotesTreeNodes]);

  const handleNewNoteDraftChange = useCallback((value: string) => {
    setNewNoteDraft(value);
    const normalizedLength = sanitizeNoteContent(value).length;
    if (normalizedLength > EXAM_NOTE_MAX_LENGTH) {
      setNewNoteError(`Note cannot exceed ${EXAM_NOTE_MAX_LENGTH} characters.`);
      return;
    }
    setNewNoteError(null);
  }, [sanitizeNoteContent]);

  const handleSaveNote = useCallback(async () => {
    if (!selectedDocumentId || !selectedNotesTopicId) {
      return;
    }

    const validationError = validateNoteContent(newNoteDraft);
    if (validationError) {
      setNewNoteError(validationError);
      return;
    }

    const content = sanitizeNoteContent(newNoteDraft);
    const topicId = selectedNotesTopicId;
    const optimisticNoteId = `temp-${Date.now()}`;
    const optimisticNote: ExamTopicNote = {
      noteId: optimisticNoteId,
      userId: '',
      examId: selectedDocumentId,
      topicId,
      content,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setIsSavingNote(true);
    setNewNoteDraft('');
    setNewNoteError(null);
    setNotesError(null);

    setNotesByTopicId((previous) => ({
      ...previous,
      [topicId]: [optimisticNote, ...(previous[topicId] || [])],
    }));

    setNoteCountsByTopic((previous) => ({
      ...previous,
      [topicId]: (previous[topicId] || 0) + 1,
    }));

    try {
      const savedNote = await createTopicNote(selectedDocumentId, topicId, content);

      setNotesByTopicId((previous) => ({
        ...previous,
        [topicId]: (previous[topicId] || []).map((note) =>
          note.noteId === optimisticNoteId ? savedNote : note
        ),
      }));

      cachedNotesTopicsRef.current.add(topicId);
    } catch (error) {
      setNotesByTopicId((previous) => ({
        ...previous,
        [topicId]: (previous[topicId] || []).filter((note) => note.noteId !== optimisticNoteId),
      }));

      setNoteCountsByTopic((previous) => ({
        ...previous,
        [topicId]: Math.max(0, (previous[topicId] || 1) - 1),
      }));

      setNewNoteDraft(content);
      setNotesError(getApiErrorMessage(error, 'Failed to save note.'));
    } finally {
      setIsSavingNote(false);
    }
  }, [
    selectedDocumentId,
    selectedNotesTopicId,
    newNoteDraft,
    validateNoteContent,
    sanitizeNoteContent,
    getApiErrorMessage,
  ]);

  const handleStartEditNote = useCallback((noteId: string, content: string) => {
    setEditingNoteId(noteId);
    setEditingNoteDraft(content);
    setEditingNoteError(null);
    setNotesError(null);
  }, []);

  const handleEditingNoteDraftChange = useCallback((value: string) => {
    setEditingNoteDraft(value);
    const normalizedLength = sanitizeNoteContent(value).length;
    if (normalizedLength > EXAM_NOTE_MAX_LENGTH) {
      setEditingNoteError(`Note cannot exceed ${EXAM_NOTE_MAX_LENGTH} characters.`);
      return;
    }
    setEditingNoteError(null);
  }, [sanitizeNoteContent]);

  const handleCancelEditingNote = useCallback(() => {
    setEditingNoteId(null);
    setEditingNoteDraft('');
    setEditingNoteError(null);
  }, []);

  const handleUpdateNote = useCallback(async () => {
    if (!editingNoteId || !selectedNotesTopicId) {
      return;
    }

    const validationError = validateNoteContent(editingNoteDraft);
    if (validationError) {
      setEditingNoteError(validationError);
      return;
    }

    const topicId = selectedNotesTopicId;
    const normalizedContent = sanitizeNoteContent(editingNoteDraft);
    const currentNotes = notesByTopicId[topicId] || [];
    const previousNote = currentNotes.find((note) => note.noteId === editingNoteId);

    if (!previousNote) {
      return;
    }

    const optimisticUpdatedNote: ExamTopicNote = {
      ...previousNote,
      content: normalizedContent,
      updatedAt: new Date().toISOString(),
    };

    setUpdatingNoteId(editingNoteId);
    setEditingNoteError(null);
    setNotesError(null);

    setNotesByTopicId((previous) => ({
      ...previous,
      [topicId]: (previous[topicId] || []).map((note) =>
        note.noteId === editingNoteId ? optimisticUpdatedNote : note
      ),
    }));

    try {
      const updated = await updateTopicNote(editingNoteId, normalizedContent);
      setNotesByTopicId((previous) => ({
        ...previous,
        [topicId]: (previous[topicId] || []).map((note) =>
          note.noteId === editingNoteId ? updated : note
        ),
      }));
      setEditingNoteId(null);
      setEditingNoteDraft('');
    } catch (error) {
      setNotesByTopicId((previous) => ({
        ...previous,
        [topicId]: (previous[topicId] || []).map((note) =>
          note.noteId === editingNoteId ? previousNote : note
        ),
      }));
      setNotesError(getApiErrorMessage(error, 'Failed to update note.'));
    } finally {
      setUpdatingNoteId(null);
    }
  }, [
    editingNoteId,
    selectedNotesTopicId,
    editingNoteDraft,
    validateNoteContent,
    sanitizeNoteContent,
    notesByTopicId,
    getApiErrorMessage,
  ]);

  const handleDeleteNote = useCallback(async (noteId: string) => {
    if (!selectedNotesTopicId) {
      return;
    }

    const topicId = selectedNotesTopicId;
    const currentNotes = notesByTopicId[topicId] || [];
    const deletedCandidate = currentNotes.find((note) => note.noteId === noteId);

    if (!deletedCandidate) {
      return;
    }

    setDeletingNoteId(noteId);
    setNotesError(null);

    setNotesByTopicId((previous) => ({
      ...previous,
      [topicId]: (previous[topicId] || []).filter((note) => note.noteId !== noteId),
    }));

    setNoteCountsByTopic((previous) => ({
      ...previous,
      [topicId]: Math.max(0, (previous[topicId] || 1) - 1),
    }));

    if (editingNoteId === noteId) {
      setEditingNoteId(null);
      setEditingNoteDraft('');
      setEditingNoteError(null);
    }

    try {
      await deleteTopicNote(noteId);
    } catch (error) {
      setNotesByTopicId((previous) => {
        const restored = [deletedCandidate, ...(previous[topicId] || [])];
        restored.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        return {
          ...previous,
          [topicId]: restored,
        };
      });

      setNoteCountsByTopic((previous) => ({
        ...previous,
        [topicId]: (previous[topicId] || 0) + 1,
      }));

      setNotesError(getApiErrorMessage(error, 'Failed to delete note.'));
    } finally {
      setDeletingNoteId(null);
    }
  }, [selectedNotesTopicId, notesByTopicId, editingNoteId, getApiErrorMessage]);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      if (isExamMode) {
        // Exam mode: Fetch exam-mode chats only
        if (!selectedDocumentId || !selectedSubject || !selectedExamName || !incompleteTopic || !incompleteSubtopic) {
          return;
        }

        try {
          console.log('🔍 Fetching exam mode chats for:', { selectedDocumentId, selectedSubject, selectedExamName, topic: incompleteTopic, subtopic: incompleteSubtopic });
          const url = `${process.env.REACT_APP_API_URL}/api/llm/exam-mode-chats?courseId=${encodeURIComponent(
            selectedDocumentId
          )}&subject=${encodeURIComponent(selectedSubject)}&examName=${encodeURIComponent(
            selectedExamName
          )}&topic=${encodeURIComponent(incompleteTopic)}&subtopic=${encodeURIComponent(incompleteSubtopic)}`;

          const res = await fetch(url, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: getAbortSignal(),
          });

          if (!res.ok) {
            throw new Error(`Request failed with status ${res.status}`);
          }

          const data = await res.json();
          if (data.success && Array.isArray(data.data)) {
            const convertedMessages: Message[] = data.data.map((msg: any, index: number) => ({
              role: msg.role,
              content: msg.content || '',
              isUser: msg.role === 'user',
              timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
              images: msg.imageUrls?.map((url: string, imgIndex: number) => ({
                file: new File([], `image-${index}-${imgIndex}`, { type: 'image/png' }),
                preview: url,
                id: `exam-chat-img-${msg._id}-${imgIndex}`,
              })),
              audio: msg.audioUrl ? { audioUrl: msg.audioUrl, duration: msg.audioDuration || 0 } : undefined,
            }));
            setMessages(hideFirstUserMessage(convertedMessages));
            console.log('✅ Exam mode messages loaded:', convertedMessages.length);
          } else {
            resetMessagesState();
            console.log('ℹ️ No exam mode messages found');
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            console.log('❌ Fetch aborted');
          } else {
            console.error('❌ Error fetching exam mode chats:', error);
          }
        }
      }
    };

    fetchData();
  }, [isExamMode, selectedDocumentId, selectedSubject, selectedExamName, incompleteTopic, incompleteSubtopic]);

  // Initialize completion state from examResult
  useEffect(() => {
    if (examResult?.topics) {
      const initialCompletedTopics: Record<string, boolean> = {};
      const initialCompletedSubtopics: Record<string, Record<string, boolean>> = {};

      examResult.topics.forEach((topic: any, topicIndex: number) => {
        const topicKey = `${topic.topic}-${topicIndex}`;
        initialCompletedTopics[topicKey] = topic.completed || false;

        if (topic.subtopics) {
          const subtopicsRecord: Record<string, boolean> = {};
          topic.subtopics.forEach((subtopic: any, subtopicIndex: number) => {
            const subtopicKey = `${topicKey}-${subtopic.name}-${subtopicIndex}`;
            subtopicsRecord[subtopicKey] = subtopic.completed || false;
          });
          initialCompletedSubtopics[topicKey] = subtopicsRecord;
        }
      });

      setCompletedTopics(initialCompletedTopics);
      setCompletedSubtopics(initialCompletedSubtopics);
    }
  }, [examResult]);

  // Update completion state when examResult changes
  useEffect(() => {
    if (examResult?.topics) {
      const newCompletedTopics: Record<string, boolean> = {};
      const newCompletedSubtopics: Record<string, Record<string, boolean>> = {};

      examResult.topics.forEach((topic: any, topicIndex: number) => {
        const topicKey = `${topic.topic}-${topicIndex}`;
        // Only update if not already in state
        if (completedTopics[topicKey] === undefined) {
          newCompletedTopics[topicKey] = topic.completed || false;
        }

        if (topic.subtopics) {
          const subtopicsRecord: Record<string, boolean> = {};
          topic.subtopics.forEach((subtopic: any, subtopicIndex: number) => {
            const subtopicKey = `${topicKey}-${subtopic.name}-${subtopicIndex}`;
            // Only update if not already in state
            if (!completedSubtopics[topicKey] || completedSubtopics[topicKey][subtopicKey] === undefined) {
              subtopicsRecord[subtopicKey] = subtopic.completed || false;
            }
          });
          if (Object.keys(subtopicsRecord).length > 0) {
            newCompletedSubtopics[topicKey] = { ...completedSubtopics[topicKey], ...subtopicsRecord };
          }
        }
      });

      if (Object.keys(newCompletedTopics).length > 0) {
        setCompletedTopics(prev => ({ ...prev, ...newCompletedTopics }));
      }

      if (Object.keys(newCompletedSubtopics).length > 0) {
        setCompletedSubtopics(prev => {
          const updated = { ...prev };
          Object.keys(newCompletedSubtopics).forEach(topicKey => {
            updated[topicKey] = { ...updated[topicKey], ...newCompletedSubtopics[topicKey] };
          });
          return updated;
        });
      }
    }
  }, [examResult]);

  useEffect(() => {
    const handleNavigation = () => {
      if (botAudioRef.current) {
        botAudioRef.current.pause();
        botAudioRef.current.currentTime = 0;
        botAudioRef.current = null;
      }
      setIsAudioPlaying(false);
      setIsBotAudioManuallyPlaying(false);
      setLastBotAudio(null);
      // Abort any ongoing API calls during navigation
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };

    window.addEventListener('popstate', handleNavigation);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, []);

  // Toggle topic completion
  const toggleTopicCompletion = (topicKey: string) => {
    setCompletedTopics(prev => ({
      ...prev,
      [topicKey]: prev[topicKey] !== undefined ? !prev[topicKey] : true
    }));
  };

  // Toggle subtopic completion  
  const toggleSubtopicCompletion = (topicKey: string, subtopicKey: string) => {
    setCompletedSubtopics(prev => ({
      ...prev,
      [topicKey]: {
        ...prev[topicKey],
        [subtopicKey]: prev[topicKey]?.[subtopicKey] !== undefined
          ? !prev[topicKey][subtopicKey]
          : true
      }
    }));
  };


  // Saved exam topics list state
  const [isMyTopicsOpen, setIsMyTopicsOpen] = useState(false);
  const [isLoadingMyTopics, setIsLoadingMyTopics] = useState(false);
  const [myTopicsError, setMyTopicsError] = useState<string | null>(null);
  const [myTopicsList, setMyTopicsList] = useState<any[]>([]);

  // State to track if the current exam result is from saved topics
  const [isFromSavedTopics, setIsFromSavedTopics] = useState(false);

  // Function to find incomplete topics from API
  const findIncompleteTopics = async (subject: string, examName: string) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found. Please login first.');
      return null;
    }

    try {
      setIsLoadingIncomplete(true);
      const url = `${process.env.REACT_APP_API_URL}/api/exam-topics/incomplete`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ subject, examName }),
        signal: getAbortSignal(),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed with status ${res.status}`);
      }

      const data = await res.json();

      if (data.message === 'All topics are completed') {
        setIncompleteTopic('');
        setIncompleteSubtopic('');
        console.log('🎉 All topics are completed!');
        return { topic: null, subtopic: null, allCompleted: true };
      }

      if (data.topic) {
        setIncompleteTopic(data.topic);
        setIncompleteSubtopic(data.subtopic || '');
        console.log('📚 Incomplete Topic Found:', data.topic);
        console.log('📖 Incomplete Subtopic:', data.subtopic || 'None');
        return { topic: data.topic, subtopic: data.subtopic, allCompleted: false };
      }

      return null;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        console.error('Error finding incomplete topics:', error);
      }
      setIncompleteTopic('');
      setIncompleteSubtopic('');
      return null;
    } finally {
      setIsLoadingIncomplete(false);
    }
  };

  // Function to get next incomplete topic when opening saved topics
  const getNextIncompleteTopic = async (doc: any) => {
    const subject = doc?.subject || 'General';
    const examName = doc?.examType || doc?.examName || 'Unknown Exam';

    const result = await findIncompleteTopics(subject, examName);
    if (result && !result.allCompleted) {
      console.log('Next incomplete topic:', result.topic, 'Subtopic:', result.subtopic);
      // You can use this to highlight or scroll to the incomplete topic
    }
    return result;
  };

  // Function to trigger exam API from fetchExamModeChats when no previous messages found
  const triggerExamAPIFromFetch = async () => {
    // Guard against duplicate calls
    if (examAPITriggered.current) {
      console.log('🚫 triggerExamAPIFromFetch already triggered, skipping...');
      return;
    }

    console.log('🎯 triggerExamAPIFromFetch called - STARTING API CALL');
    console.log('🔍 About to make API call for new exam mode conversation');

    examAPITriggered.current = true; // Set guard immediately

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found. Please login first.');
      examAPITriggered.current = false; // Reset guard on error
      return;
    }
    // Check if all required parameters are present - use state values
    const currentTopic = incompleteTopic;
    const currentSubtopic = incompleteSubtopic;

    if (!selectedDocumentId || !selectedSubject || !selectedExamName || !currentTopic || !currentSubtopic) {
      console.error('❌ Missing required parameters for exam mode API call:', {
        selectedDocumentId: !!selectedDocumentId,
        subject: !!selectedSubject,
        examName: !!selectedExamName,
        currentTopic: !!currentTopic,
        currentSubtopic: !!currentSubtopic,
        incompleteTopic: !!incompleteTopic,
        incompleteSubtopic: !!incompleteSubtopic
      });
      examAPITriggered.current = false; // Reset guard on error
      return;
    }
    try {
      // Create FormData for the exam mode initial message
      const formData = new FormData();
      const firstMsg = uiText.defineAbout(currentSubtopic);
      formData.append('question', firstMsg);
      formData.append('subject', selectedSubject);
      formData.append('language', selectedLanguage);
      formData.append('examName', selectedExamName);
      formData.append('topic', currentTopic);
      formData.append('subtopic', currentSubtopic);
      formData.append('courseId', selectedDocumentId);
      formData.append('examMode', 'true');

      console.log('📤 Sending exam mode request with:', {
        question: firstMsg,
        subject: selectedSubject,
        examName: selectedExamName,
        topic: currentTopic,
        subtopic: currentSubtopic,
        courseId: selectedDocumentId
      });
      // Add loading message with typing indicator
      const loadingMessage: Message = {
        role: 'assistant',
        content: '',
        isUser: false,
        timestamp: new Date()
      };

      setMessages([loadingMessage]);
      console.log('📤 Sending exam mode request to API from fetch...');
      console.log('🔍 FormData contents:', {
        question: firstMsg,
        subject: selectedSubject,
        examName: selectedExamName,
        topic: incompleteTopic,
        subtopic: incompleteSubtopic,
        courseId: selectedDocumentId
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}${llmGenerateEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: getAbortSignal(),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const ragImageUrls = getRagImageUrlsFromResponse(response);
      console.log("image......................", ragImageUrls);
      attachImagesToLatestAssistantMessage(ragImageUrls, 'exam-mode-init');

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response reader available');
      }
      attachImagesToLatestAssistantMessage(ragImageUrls, 'chat-rag');
      console.log('Starting to read exam mode stream from fetch...');
      let accumulatedContent = '';
      const decoder = new TextDecoder('utf-8');
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          const finalChunk = decoder.decode();
          if (finalChunk) {
            accumulatedContent += finalChunk;
          }
          applyBufferedAssistantContent(accumulatedContent, { final: true });
          break;
        }
        // Convert the chunk to text
        const chunk = decoder.decode(value, { stream: true });
        accumulatedContent += chunk;
        applyBufferedAssistantContent(accumulatedContent);
        // Small delay to make streaming smoother
        await new Promise(resolve => setTimeout(resolve, 10));
      }
      console.log('✅ Exam mode stream from fetch completed successfully');
      console.log('🎉 triggerExamAPIFromFetch function completed - API call successful');
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        console.error('❌ Error in exam mode API call from fetch:', error);
      }
      setMessages(messages => {
        // Remove the loading message if it exists
        const newMessages = messages.filter(msg => msg.content !== '');
        const errorMessage: Message = {
          role: 'system',
          content: uiText.errorResponse,
          isUser: false,
          timestamp: new Date()
        };
        return [...newMessages, errorMessage];
      });
      console.log('💥 triggerExamAPIFromFetch function failed');
    } finally {
      // Reset the guard after completion (success or error)
      examAPITriggered.current = false;
    }
  };



  // Function to fetch previous exam mode chat messages for specific topic/subtopic
  const fetchExamModeChats = async () => {
    // Guard against duplicate calls
    if (examModeInitialized.current) {
      console.log('🚫 fetchExamModeChats already initialized, skipping...');
      return;
    }

    console.log('🔥🔥🔥 fetchExamModeChats FUNCTION CALLED 🔥🔥🔥');
    console.log('🔍 fetchExamModeChats called with state:', {
      selectedDocumentId,
      subject: sessionSubject,
      zaptalkTopic: selectedZapTalkTopic,
      examName: selectedExamName,
      incompleteTopic,
      incompleteSubtopic
    });
    console.log('🚀 STARTING fetchExamModeChats function');

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found. Please login first.');
      return;
    }

    // Check if we have all required parameters - use state values
    const currentTopic = incompleteTopic;
    const currentSubtopic = incompleteSubtopic;

    if (!selectedDocumentId || !selectedSubject || !selectedExamName || !currentTopic || !currentSubtopic) {
      console.log('⚠️ Missing required parameters for exam mode chats:', {
        courseId: selectedDocumentId,
        subject: selectedSubject,
        examName: selectedExamName,
        topic: currentTopic,
        subtopic: currentSubtopic,
        incompleteTopic,
        incompleteSubtopic
      });
      return;
    }

    try {
      console.log('🔍 Fetching exam mode chats for:', {
        courseId: selectedDocumentId,
        subject: selectedSubject,
        examName: selectedExamName,
        topic: currentTopic,
        subtopic: currentSubtopic
      });

      const url = `${process.env.REACT_APP_API_URL}/api/llm/exam-mode-chats?courseId=${encodeURIComponent(selectedDocumentId)}&subject=${encodeURIComponent(selectedSubject)}&examName=${encodeURIComponent(selectedExamName)}&topic=${encodeURIComponent(currentTopic)}&subtopic=${encodeURIComponent(currentSubtopic)}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: getAbortSignal(),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed with status ${res.status}`);
      }

      const data = await res.json();
      console.log('📊 Exam mode chats response:', data);
      console.log('🔍 Response analysis:', {
        success: data.success,
        hasData: !!data.data,
        isArray: Array.isArray(data.data),
        dataLength: data.data ? data.data.length : 0
      });

      if (data.success && data.data && Array.isArray(data.data)) {
        // Convert backend messages to frontend Message format
        const convertedMessages: Message[] = data.data.map((msg: any, index: number) => {
          // Convert imageUrls to ImagePreview format if they exist
          let images: ImagePreview[] | undefined = undefined;
          if (msg.imageUrls && Array.isArray(msg.imageUrls) && msg.imageUrls.length > 0) {
            images = msg.imageUrls.map((url: string, imgIndex: number) => ({
              file: new File([], `image-${index}-${imgIndex}`, { type: 'image/png' }),
              preview: url,
              id: `exam-chat-img-${msg._id}-${imgIndex}`
            }));
          }

          // Convert audioUrl to audio format if it exists
          let audio: { audioUrl: string; duration: number } | undefined = undefined;
          if (msg.audioUrl && typeof msg.audioUrl === 'string') {
            audio = {
              audioUrl: msg.audioUrl,
              duration: msg.audioDuration || 0
            };
          }

          return {
            role: msg.role,
            content: msg.content || '',
            isUser: msg.role === 'user',
            timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
            images: images,
            audio: audio
          };
        });

        setMessages(hideFirstUserMessage(convertedMessages));
        console.log('✅ Exam mode messages loaded:', convertedMessages.length, 'messages');

        // Add welcome back message when previous conversations are found
        if (convertedMessages.length > 0) {
          const welcomeMessage: Message = {
            role: 'assistant',
            content: uiText.welcomeWithSubtopic(currentSubtopic),
            isUser: false,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, welcomeMessage]);
          console.log('👋 Welcome back message added');
        }
        else {
          // Only trigger API if not already triggered
          if (!examAPITriggered.current) {
            triggerExamAPIFromFetch();
          } else {
            console.log('🚫 triggerExamAPIFromFetch already triggered, skipping...');
          }
        }

        // Mark exam mode as initialized since we loaded existing messages
        console.log('🏁 Exam mode initialized with existing messages');
      } else {
        console.log('ℹ️ No previous exam mode messages found for this topic/subtopic, will trigger exam API');
        console.log('🎯 ENTERING ELSE BLOCK - No previous messages found');
        resetMessagesState();
        // Trigger exam API when no previous messages are found
        console.log('🚀 Calling triggerExamAPIFromFetch...');
        try {
          // Only trigger API if not already triggered
          if (!examAPITriggered.current) {
            await triggerExamAPIFromFetch();
            console.log('✅ triggerExamAPIFromFetch completed successfully');
            console.log('🏁 Exam mode initialization completed with new API call');
          } else {
            console.log('🚫 triggerExamAPIFromFetch already triggered, skipping...');
          }
        } catch (error) {
          console.error('❌ Error in triggerExamAPIFromFetch:', error);
          // Reset the initialization flag on error so it can retry
          examModeInitialized.current = false;
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        console.error('❌ Error fetching exam mode chats:', error);
      }
      resetMessagesState();
    }
  };

  // Helper function to get exam name from current exam result
  const getCurrentExamName = () => {
    if (examResult?.examName) return examResult.examName;
    if (examResult?.examType) return examResult.examType;
    if (examResult?.subject) return examResult.subject;
    return 'Unknown Exam';
  };

  // Helper function to get subject from current exam result
  const getCurrentSubject = () => {
    return examResult?.subject || 'General';
  };

  const unwrapApiPayload = (payload: any): any => {
    if (payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object') {
      return payload.data;
    }
    return payload;
  };

  const resolveExamDocumentId = async (
    idCandidates: unknown[],
    subject: string,
    examName: string
  ): Promise<string> => {
    for (const candidate of idCandidates) {
      if (isMongoObjectIdValue(candidate)) {
        return candidate.trim();
      }
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return '';
    }

    try {
      const url = `${process.env.REACT_APP_API_URL}/api/exam-topics/mine?subject=${encodeURIComponent(subject)}&limit=20&page=1`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return '';
      }

      const body = await response.json();
      const rows = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
      const normalizedExamName = examName.trim().toLowerCase();
      const normalizedSubject = subject.trim().toLowerCase();

      const exactMatch = rows.find((row: any) =>
        isMongoObjectIdValue(row?._id) &&
        String(row?.examName || '').trim().toLowerCase() === normalizedExamName &&
        String(row?.subject || '').trim().toLowerCase() === normalizedSubject
      );

      if (exactMatch?._id) {
        return exactMatch._id;
      }

      const fallbackMatch = rows.find((row: any) => isMongoObjectIdValue(row?._id));
      return fallbackMatch?._id || '';
    } catch (error) {
      console.error('Failed to resolve exam document id:', error);
      return '';
    }
  };

  // Add the sendInitialMessage function
  const sendInitialMessage = async (currentSessionId: string) => {
    if (requestInProgress.current) return;

    console.log('Attempting to send initial message...');

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found. Please login.');
      return;
    }

    try {
      requestInProgress.current = true;
      console.log('Creating initial message with sessionId:', currentSessionId);

      // Create FormData for the initial message
      const formData = new FormData();
      if (isExamMode) {
        const firstMsg = uiText.defineAbout(incompleteSubtopic);
        formData.append('question', firstMsg);
        formData.append('subject', selectedSubject);
        formData.append('language', selectedLanguage);
        formData.append('examName', selectedExamName);
        formData.append('topic', incompleteTopic);
        formData.append('subtopic', incompleteSubtopic);
        formData.append('courseId', selectedDocumentId);
        formData.append('examMode', 'true');
      }
      else {
        formData.append('question', initialGreeting);
        formData.append('subject', sessionSubject);
        formData.append('language', selectedLanguage);
        formData.append('sessionId', currentSessionId);
        formData.append('examMode', 'false');
        if (isZapTalkMode) {
          formData.append('topic', zaptalkTopic);
          formData.append('chatMode', 'zaptalk');
        }
      }

      // Add loading message without showing the "Hi" message
      const loadingMessage: Message = {
        role: 'assistant',
        content: '',
        isUser: false,
        timestamp: new Date()
      };

      if (isMounted.current) {
        setMessages(prev => [...prev, loadingMessage]);
      }

      console.log('Sending request to API...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}${llmGenerateEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: getAbortSignal(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const ragImageUrls = getRagImageUrlsFromResponse(response);
      console.log("IMAGE...", ragImageUrls);
      attachImagesToLatestAssistantMessage(ragImageUrls, 'session-init');

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response reader available');
      }

      console.log('Starting to read stream...');
      let accumulatedContent = '';
      const decoder = new TextDecoder('utf-8');
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          const finalChunk = decoder.decode();
          if (finalChunk) {
            accumulatedContent += finalChunk;
          }
          if (isMounted.current) {
            applyBufferedAssistantContent(accumulatedContent, { final: true });
          }
          break;
        }

        // Convert the chunk to text
        const chunk = decoder.decode(value, { stream: true });
        accumulatedContent += chunk;

        if (isMounted.current) {
          applyBufferedAssistantContent(accumulatedContent);
        }
      }
      console.log('Stream completed successfully');

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        console.error('Error sending initial message:', error);
      }
      if (isMounted.current) {
        setMessages(messages => messages.filter(msg => msg.content !== '')); // Remove loading message if error
      }
    } finally {
      requestInProgress.current = false;
    }
  };

  // Function to fetch latest sessions
  const fetchLatestSessions = async (page: number = 1) => {
    setIsLoadingSessions(true);
    setSessionsError(null);

    try {
      // Ensure we have a valid subject
      const subjectToUse = sessionSubject || 'General';
      console.log('📋 Fetching sessions with subject:', subjectToUse);

      const response = await getLatestSessions(
        sessionsPerPage,
        undefined,
        undefined,
        page,
        undefined,
        subjectToUse
      );
      if (response && response.success && response.data) {
        console.log('✅ Sessions fetched successfully:', response.data.length, 'sessions');
        setLatestSessions(response.data);
        setCurrentPage(response.pagination.page);
        setTotalPages(response.pagination.totalPages);
        setTotalSessions(response.pagination.total);
        setHasNextPage(response.pagination.hasNextPage);
        setHasPrevPage(response.pagination.hasPrevPage);
      } else {
        console.log('⚠️ No sessions found or failed to load');
        setSessionsError('Failed to load sessions');
      }
    } catch (error) {
      console.error('❌ Error fetching latest sessions:', error);
      setSessionsError(error instanceof Error ? error.message : 'Failed to load sessions');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Function to handle date filter
  const handleDateFilter = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('Start date cannot be later than end date');
      return;
    }

    setIsLoadingSessions(true);
    setSessionsError(null);

    try {
      const response = await getLatestSessions(
        sessionsPerPage,
        startDate,
        endDate,
        1,
        undefined,
        sessionSubject
      );
      if (response && response.success && response.data) {
        setFilteredSessions(response.data);
        setLatestSessions(response.data);
        setCurrentPage(response.pagination.page);
        setTotalPages(response.pagination.totalPages);
        setTotalSessions(response.pagination.total);
        setHasNextPage(response.pagination.hasNextPage);
        setHasPrevPage(response.pagination.hasPrevPage);
        console.log('Filtered sessions:', response);
      } else {
        setSessionsError('No sessions found in the selected date range');
        setFilteredSessions([]);
        setLatestSessions([]);
      }
    } catch (error) {
      console.error('Error filtering sessions by date:', error);
      setSessionsError(error instanceof Error ? error.message : 'Failed to filter sessions');
      setFilteredSessions([]);
    } finally {
      setIsLoadingSessions(false);
      setIsDateFilterOpen(false);
    }
  };

  // Function to clear date filter
  const clearDateFilter = () => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
    setEndDate(today);
    setFilteredSessions([]);
    setIsDateFilterOpen(false);
    fetchLatestSessions(1); // Reload all sessions
  };

  // Pagination functions
  const handleNextPage = () => {
    if (hasNextPage) {
      fetchLatestSessions(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      fetchLatestSessions(currentPage - 1);
    }
  };

  const handlePageChange = (page: number) => {
    fetchLatestSessions(page);
  };



  // Function to handle session selection
  const handleSessionSelect = async (sessionId: string) => {
    try {
      resetMessagesState();
      setIsLoadingSessions(true);

      setSessionId(sessionId);
      setSelectedSessionId(sessionId);

      const sessionData = await getSessionMessages(sessionId, {
        limit: MESSAGE_PAGE_SIZE,
      });

      if (sessionData && sessionData.success && sessionData.data) {
        const { messages: sessionMessages, topic: sessionTopicFromApi } = sessionData.data;
        if (isZapTalkMode) {
          let resolvedZapTalkTopic = typeof sessionTopicFromApi === 'string' && sessionTopicFromApi.trim()
            ? sessionTopicFromApi.trim()
            : '';

          if (!resolvedZapTalkTopic) {
            const matchedSession = latestSessions.find((session) => session._id === sessionId);
            if (typeof matchedSession?.topic === 'string' && matchedSession.topic.trim()) {
              resolvedZapTalkTopic = matchedSession.topic.trim();
            }
          }

          if (!resolvedZapTalkTopic && Array.isArray(sessionMessages)) {
            for (let index = sessionMessages.length - 1; index >= 0; index -= 1) {
              const value = sessionMessages[index]?.topic;
              if (typeof value === 'string' && value.trim()) {
                resolvedZapTalkTopic = value.trim();
                break;
              }
            }
          }

          if (resolvedZapTalkTopic && resolvedZapTalkTopic !== selectedZapTalkTopic) {
            setSelectedZapTalkTopic(resolvedZapTalkTopic);
          }
        }

        const convertedMessages = convertSessionMessages(sessionMessages || []);

        setMessages(convertedMessages);

        const pagination = sessionData.pagination;
        setHasMoreMessages(pagination?.hasMore ?? false);
        setMessagesCursor(pagination?.nextCursor ?? null);

        console.log('Loaded messages for session:', sessionId, convertedMessages.length);
      } else {
        console.error('Failed to load session messages:', sessionData);
        resetMessagesState();
      }

      if (window.innerWidth <= 768) {
        setIsSidebarOpen(false);
      }

      console.log('Switched to session:', sessionId);
      if (isZapTalkMode) {
        const welcomeBackMessage: Message = {
          role: 'assistant',
          content: ZAPTALK_WELCOME_BACK_MESSAGE,
          isUser: false,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, welcomeBackMessage]);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        console.error('Error switching to session:', error);
      }
      resetMessagesState();
      const errorMessage: Message = {
        role: 'system',
        content: uiText.errorLoad,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages([errorMessage]);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const handleLoadOlderMessages = async () => {
    if (!sessionId || isLoadingOlderMessages || !hasMoreMessages || !messagesCursor) {
      return;
    }

    try {
      setIsLoadingOlderMessages(true);
      const container = messagesContainerRef.current;
      const previousScrollHeight = container?.scrollHeight ?? 0;

      const sessionData = await getSessionMessages(sessionId, {
        limit: MESSAGE_PAGE_SIZE,
        before: messagesCursor,
      });

      if (sessionData && sessionData.success && sessionData.data) {
        const sessionMessages = sessionData.data.messages || [];
        if (sessionMessages.length === 0) {
          setHasMoreMessages(false);
          setMessagesCursor(null);
        } else {
          const olderMessages = convertSessionMessages(sessionMessages);
          preventAutoScrollRef.current = true;
          setMessages((prev) => [...olderMessages, ...prev]);

          const pagination = sessionData.pagination;
          setHasMoreMessages(pagination?.hasMore ?? false);
          setMessagesCursor(pagination?.nextCursor ?? null);

          requestAnimationFrame(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              container.scrollTop = newScrollHeight - previousScrollHeight;
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading older messages:', error);
    } finally {
      setIsLoadingOlderMessages(false);
    }
  };

  const createNewSession = async (overrideZapTalkTopic?: string): Promise<boolean> => {
    if (requestInProgress.current) return false;

    const resolvedZapTalkTopic =
      typeof overrideZapTalkTopic === 'string' && overrideZapTalkTopic.trim()
        ? overrideZapTalkTopic.trim()
        : selectedZapTalkTopic.trim();

    if (isZapTalkMode && !resolvedZapTalkTopic) {
      setIsZapTalkCategoryModalOpen(true);
      return false;
    }

    console.log('Starting session creation...');

    try {
      requestInProgress.current = true;
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found. Please login.');
        return false;
      }

      // Create new session
      console.log('Making session creation API call...');
      console.log('Creating session with subject:', sessionSubject);

      const sessionResponse = (
        await api.post('/session/create', {
          subject: sessionSubject,
          ...(isZapTalkMode ? { topic: resolvedZapTalkTopic, chatMode: 'zaptalk' } : {}),
        }, {
          signal: getAbortSignal(),
        })
      ).data;

      console.log('Session creation response:', sessionResponse);

      if (sessionResponse && sessionResponse.success && isMounted.current) {
        const { sessionId, isExisting } = sessionResponse.data;

        if (isExisting) {
          // Reusing existing session - load its messages instead of sending initial message
          console.log('Reusing existing session:', sessionId);
          requestInProgress.current = false;
          // Store the session ID received from backend
          setSessionId(sessionId);
          setSelectedSessionId(sessionId);

          // Load the existing session messages (which already include "Hi" + AI response)
          await handleSessionSelect(sessionId);

          // Refresh the latest sessions list
          fetchLatestSessions(1);
          return true;
        } else {
          // New session created - send initial message
          console.log('Session created successfully:', sessionId);
          requestInProgress.current = false;
          // Store the session ID received from backend
          setSessionId(sessionId);

          // Send initial message only for newly created sessions
          console.log('Sending initial message for normal mode...');
          await sendInitialMessage(sessionId);

          // Refresh the latest sessions list to include the new session AFTER initial message
          fetchLatestSessions(1);
          return true;
        }
      } else {
        console.error('Session creation failed:', sessionResponse);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.error('Failed to create new session:', error);
      }
    } finally {
      requestInProgress.current = false;
    }

    return false;
  };

  const resolveZapTalkSessionByTopic = async (topic: string) => {
    const resolvedTopic = topic.trim() || ZAPTALK_DEFAULT_TOPIC;
    const normalizedTopic = resolvedTopic.toLowerCase();
    const hasMatchingTopic = (session: SessionListItem) =>
      typeof session.topic === 'string' && session.topic.trim().toLowerCase() === normalizedTopic;

    setSelectedZapTalkTopic(resolvedTopic);
    setSelectedZapTalkCategory('');
    setIsZapTalkCategoryModalOpen(false);
    sessionCreated.current = true;

    const localMatch = latestSessions.find(hasMatchingTopic);
    if (localMatch) {
      if (selectedSessionId === localMatch._id) {
        return;
      }
      await handleSessionSelect(localMatch._id);
      return;
    }

    try {
      const response = await getLatestSessions(
        100,
        undefined,
        undefined,
        1,
        undefined,
        ZAPTALK_SESSION_SUBJECT
      );
      const remoteSessions = Array.isArray(response?.data) ? response.data : [];
      const remoteMatch = remoteSessions.find(hasMatchingTopic);

      if (remoteMatch) {
        setLatestSessions(remoteSessions.slice(0, sessionsPerPage));
        if (selectedSessionId === remoteMatch._id) {
          return;
        }
        await handleSessionSelect(remoteMatch._id);
        return;
      }
    } catch (error) {
      console.error('Failed to fetch ZapTalk sessions for topic reuse:', error);
    }

    resetMessagesState();
    setSessionId(null);
    setSelectedSessionId(null);
    const created = await createNewSession(resolvedTopic);
    if (!created) {
      sessionCreated.current = false;
    }
  };

  useEffect(() => {
    isMounted.current = true;

    // Debug log for exam mode initialization
    console.log('🚀 Chat component mounted with state:', {
      isExamMode,
      courseId: selectedDocumentId,
      subject: sessionSubject,
      examName: selectedExamName,
      selectedDocumentId,
      incompleteTopic,
      incompleteSubtopic,
      sessionId,
      sessionCreated: sessionCreated.current,
      existingSessionId
    });



    // Handle exam mode initialization
    if (isExamMode && selectedDocumentId && selectedExamName && incompleteTopic && incompleteSubtopic && !sessionCreated.current && !examModeInitialized.current) {
      console.log('🎯 Exam mode detected, initializing...');
      sessionCreated.current = true; // Mark that we've started initialization
      // The fetchExamModeChats useEffect will handle the actual initialization
    }
    // Handle normal mode session creation
    else if (
      !isExamMode &&
      !sessionId &&
      !sessionCreated.current &&
      !existingSessionId &&
      sessionSubject &&
      (!isZapTalkMode || isZapTalkTopicReady)
    ) {
      console.log('No session ID found, creating new session...');
      console.log('Creating session with subject:', sessionSubject);
      sessionCreated.current = true; // Mark that we've started creating a session
      if (isZapTalkMode) {
        void resolveZapTalkSessionByTopic(selectedZapTalkTopic || ZAPTALK_DEFAULT_TOPIC);
      } else {
        createNewSession();
      }
    } else if (existingSessionId && !sessionCreated.current) {
      // If we have an existing sessionId, set it and load the session
      console.log('Using existing session ID:', existingSessionId);
      setSessionId(existingSessionId);
      setSelectedSessionId(existingSessionId);
      sessionCreated.current = true;
      // Load the existing session messages
      handleSessionSelect(existingSessionId);
    } else {
      console.log('⏭️ No action needed for session creation:', {
        isExamMode,
        sessionId,
        sessionCreated: sessionCreated.current,
        existingSessionId,
        selectedSubject: sessionSubject,
        selectedZapTalkTopic
      });
    }

    // Cleanup function
    return () => {
      isMounted.current = false;
      requestInProgress.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [
    isExamMode,
    selectedSubject,
    selectedDocumentId,
    selectedExamName,
    incompleteTopic,
    incompleteSubtopic,
    sessionSubject,
    isZapTalkMode,
    isZapTalkTopicReady,
    selectedZapTalkTopic
  ]); // Include exam mode dependencies

  // Fetch latest sessions when component mounts or subject changes
  useEffect(() => {
    if (sessionSubject) {
      console.log('🔄 Fetching sessions for subject:', sessionSubject);
      fetchLatestSessions(1);
    } else if (!isZapTalkMode) {
      // If no subject is set yet, wait a bit and try again
      console.log('⏳ No subject set yet, waiting for subject to be set...');
      const timer = setTimeout(() => {
        if (!selectedSubject) {
          console.log('⏰ Still no subject, fetching with default General');
          fetchLatestSessions(1);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [selectedSubject, isZapTalkMode, sessionSubject]);

  // Consolidated exam mode initialization - handles both mount and parameter changes
  useEffect(() => {
    console.log("🔍 Exam mode check:", {
      isExamMode,
      selectedDocumentId,
      selectedExamName,
      incompleteTopic,
      incompleteSubtopic,
      examModeInitialized: examModeInitialized.current
    });

    // Only proceed if we're in exam mode and have all required parameters
    if (isExamMode && selectedDocumentId && selectedExamName && incompleteTopic && incompleteSubtopic && !examModeInitialized.current) {
      console.log('🚀 Exam mode initialization detected');
      console.log('📋 Parameters:', {
        selectedDocumentId,
        selectedExamName,
        incompleteTopic,
        incompleteSubtopic
      });

      // Mark as initialized immediately to prevent duplicate calls
      examModeInitialized.current = true;

      // Small delay to ensure state is properly set
      setTimeout(() => {
        console.log('⏰ Triggering fetchExamModeChats after state update');
        fetchExamModeChats();
      }, 100);
    } else if (isExamMode && examModeInitialized.current) {
      console.log('⏭️ Exam mode already initialized, skipping...');
    }
  }, [isExamMode, selectedDocumentId, selectedExamName, incompleteTopic, incompleteSubtopic]);

  // Clear messages when topic/subtopic changes to load fresh chats
  useEffect(() => {
    if (isExamMode && (incompleteTopic || incompleteSubtopic)) {
      console.log('🔄 Topic/Subtopic changed, clearing messages for fresh chat');
      resetMessagesState();
      // Reset the initialization flags when topic/subtopic changes to allow fresh API calls
      examModeInitialized.current = false;
      examAPITriggered.current = false;
    }
  }, [incompleteTopic, incompleteSubtopic]);

  // Reset session creation when subject changes in normal mode
  useEffect(() => {
    if (!isExamMode && sessionSubject) {
      console.log('🔄 Subject changed in normal mode, resetting session creation');
      sessionCreated.current = false;
      // Also refetch sessions for the new subject
      console.log('🔄 Refetching sessions for new subject:', sessionSubject);
      fetchLatestSessions(1);
    }
  }, [selectedSubject, isExamMode, sessionSubject]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (preventAutoScrollRef.current) {
      preventAutoScrollRef.current = false;
      return;
    }
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
  }, [isDarkMode]);

  // Save voice preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('selectedVoice', selectedVoice);
  }, [selectedVoice]);

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('selectedLanguage', selectedLanguage);
  }, [selectedLanguage]);

  // Helper function to get all available voices from config
  const getAllVoices = () => {
    const voices: Array<{ id: string; name: string; gender: string; accent: string }> = [];
    const config = voiceConfig.deepgram_aura_v1;

    // Add female voices
    Object.entries(config.voices.female).forEach(([id, voice]: [string, any]) => {
      voices.push({
        id,
        name: voice.name,
        gender: 'female',
        accent: voice.accent
      });
    });

    // Add male voices
    Object.entries(config.voices.male).forEach(([id, voice]: [string, any]) => {
      voices.push({
        id,
        name: voice.name,
        gender: 'male',
        accent: voice.accent
      });
    });

    return voices;
  };

  const availableVoices = getAllVoices();
  const availableLanguages = [
    'English',
    'Hindi',
    'Bengali',
    'Telugu',
    'Marathi',
    'Tamil',
    'Gujarati',
    'Kannada',
    'Malayalam',
    'Punjabi',
    'Odia',
    'Sanskrit',
  ];

  // Get current voice details
  const getCurrentVoiceDetails = () => {
    return availableVoices.find(v => v.id === selectedVoice) || availableVoices[0];
  };

  const getMediaCategoryFromMimeType = (mimeType: string): UploadMediaType | null => {
    if (mimeType.startsWith('image/')) return 'image';
    return null;
  };

  const readFileAsDataURL = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string) || '');
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });

  const compressImageFile = async (file: File): Promise<File> => {
    try {
      const sourceUrl = URL.createObjectURL(file);
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Unable to load image.'));
        img.src = sourceUrl;
      });

      const maxSide = Math.max(image.width, image.height) || 1;
      const scale = Math.min(1, IMAGE_MAX_DIMENSION / maxSide);
      const targetWidth = Math.max(1, Math.round(image.width * scale));
      const targetHeight = Math.max(1, Math.round(image.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const context = canvas.getContext('2d');
      if (!context) {
        URL.revokeObjectURL(sourceUrl);
        return file;
      }

      context.drawImage(image, 0, 0, targetWidth, targetHeight);

      const compressedBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/webp', IMAGE_COMPRESSION_QUALITY);
      });
      URL.revokeObjectURL(sourceUrl);

      if (!compressedBlob || compressedBlob.size >= file.size) {
        return file;
      }

      const baseName = file.name.replace(/\.[^/.]+$/, '');
      return new File([compressedBlob], `${baseName || 'image'}.webp`, {
        type: 'image/webp',
        lastModified: Date.now(),
      });
    } catch (error) {
      console.warn('Image compression failed, using original image.', error);
      return file;
    }
  };

  const processMediaFile = async (file: File, mediaType: UploadMediaType): Promise<PendingMedia> => {
    const compressedFile = await compressImageFile(file);
    const preview = mediaType === 'image' ? await readFileAsDataURL(compressedFile) : undefined;

    return {
      id: `${mediaType}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: compressedFile,
      mediaType,
      name: compressedFile.name || file.name,
      originalSize: file.size,
      compressedSize: compressedFile.size,
      preview,
    };
  };

  const appendMediaFiles = async (files: File[]) => {
    if (!files.length) return;

    setMediaError(null);
    setIsMediaProcessing(true);

    const processedMedia: PendingMedia[] = [];
    try {
      for (const file of files) {
        const detectedType = getMediaCategoryFromMimeType(file.type);
        if (!detectedType) {
          continue;
        }
        const preparedFile = await processMediaFile(file, detectedType);
        processedMedia.push(preparedFile);
      }

      if (!processedMedia.length) {
        setMediaError('Only image files are supported.');
        return;
      }

      setPendingMedia((previous) => {
        const availableSlots = Math.max(0, MAX_PENDING_MEDIA - previous.length);
        if (availableSlots <= 0) {
          setMediaError(`You can attach up to ${MAX_PENDING_MEDIA} files at a time.`);
          return previous;
        }

        if (processedMedia.length > availableSlots) {
          setMediaError(`Only ${availableSlots} file(s) were added. Max ${MAX_PENDING_MEDIA} files allowed.`);
        }

        return [...previous, ...processedMedia.slice(0, availableSlots)];
      });
    } catch (error) {
      console.error('Failed to process media files:', error);
      setMediaError('Failed to process selected files. Please try again.');
    } finally {
      setIsMediaProcessing(false);
    }
  };

  const handleMediaSelection = async (files: FileList | null) => {
    if (!files?.length) return;
    await appendMediaFiles(Array.from(files));
  };

  const handlePaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i += 1) {
      if (items[i].type.includes('image')) {
        const file = items[i].getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }

    if (!imageFiles.length) {
      return;
    }

    e.preventDefault();
    await appendMediaFiles(imageFiles);
  };

  // Utility to split text into chunks for TTS (by sentence, max 800 chars)
  const splitTextForTTS = (text: string, maxLen = 800): string[] => {
    const sentences = text.match(/[^.!?]+[.!?]+[\])'"`'"`]*|.+/g) || [];
    const chunks: string[] = [];
    let current = '';
    for (const sentence of sentences) {
      if ((current + sentence).length > maxLen) {
        if (current) chunks.push(current);
        current = sentence;
      } else {
        current += sentence;
      }
    }
    if (current) chunks.push(current);
    return chunks;
  };

  const handleSendAudioMessage = async (audioBlob: Blob, audioFile: File, duration: number) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found. Please login.');
      return;
    }
    setIsResponding(true);
    try {
      console.log("audioFile", audioFile);
      const formData = new FormData();
      if (isExamMode) {
        formData.append('audio', audioFile); // The audio file
        formData.append('audioFlag', 'true'); // Use audioFlag for backend
        formData.append('subject', selectedSubject);
        formData.append('language', selectedLanguage);
        formData.append('examName', selectedExamName)
        formData.append('topic', incompleteTopic);
        formData.append('subtopic', incompleteSubtopic);
        formData.append('courseId', selectedDocumentId);
        formData.append('examMode', 'true');
      }
      else {
        formData.append('audio', audioFile); // The audio file
        formData.append('audioFlag', 'true'); // Use audioFlag for backend
        formData.append('subject', sessionSubject);
        formData.append('language', selectedLanguage);
        formData.append('sessionId', sessionId || '');
        formData.append('examMode', 'false');
        if (isZapTalkMode) {
          formData.append('topic', zaptalkTopic);
          formData.append('chatMode', 'zaptalk');
        }
      }

      // Add user message (audio) to UI
      const userMessage: Message = {
        role: 'user',
        content: '',
        isUser: true,
        timestamp: new Date(),
        audio: {
          audioUrl: URL.createObjectURL(audioBlob),
          duration
        }
      };
      setMessages(messages => [...messages, userMessage]);

      // Add loading message
      const loadingMessage: Message = {
        role: 'assistant',
        content: '',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(messages => [...messages, loadingMessage]);

      // // Send to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}${llmGenerateEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: getAbortSignal(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const ragImageUrls = getRagImageUrlsFromResponse(response);
      console.log("IMAGE....", ragImageUrls);
      attachImagesToLatestAssistantMessage(ragImageUrls, 'audio-rag');

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response reader available');
      }

      let accumulatedContent = '';
      const decoder = new TextDecoder('utf-8');

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          const finalChunk = decoder.decode();
          if (finalChunk) {
            accumulatedContent += finalChunk;
          }
          applyBufferedAssistantContent(accumulatedContent, { final: true });
          break;
        }
        const chunk = decoder.decode(value, { stream: true });
        accumulatedContent += chunk;

        applyBufferedAssistantContent(accumulatedContent);
      }

      try {
        if (!isVoiceSupported) {
          applyBufferedAssistantContent(accumulatedContent, { final: true });
          setIsLastBotResponseComplete(true);
          setIsResponding(false);
          return;
        }
        const plainText = stripMarkdown(accumulatedContent);

        // Send text chunks to TTS
        const textChunks = splitTextForTTS(plainText, 800);
        const audioBlobs: Blob[] = [];

        for (const chunk of textChunks) {
          const ttsResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/llm/generateTTS`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ text: chunk, voice: selectedVoice }),
            signal: getAbortSignal(),
          });

          if (ttsResponse.ok) {
            const arrayBuffer = await ttsResponse.arrayBuffer();
            audioBlobs.push(new Blob([arrayBuffer], { type: 'audio/wav' }));
          } else {
            console.error("TTS request failed for chunk:", chunk, await ttsResponse.text());
          }
        }

        if (audioBlobs.length > 0) {
          const combinedBlob = new Blob(audioBlobs, { type: 'audio/wav' });
          const audioUrl = URL.createObjectURL(combinedBlob);

          const audio = new Audio(audioUrl);
          botAudioRef.current = audio;
          setLastBotAudio({ audioUrl, duration: audio.duration }); // Save for manual playback
          setIsLastBotResponseComplete(false); // Mark as not complete until streaming is done

          audio.onloadedmetadata = () => {
            const totalDuration = audio.duration; // In seconds
            setLatestAssistantMessage(accumulatedContent, 'markdown', {
              audioUrl,
              duration: totalDuration
            });
            setIsLastBotResponseComplete(true);

            setIsAudioPlaying(true);
            audio.play();
            audio.onended = () => {
              setIsAudioPlaying(false);
              botAudioRef.current = null;
              setIsBotAudioManuallyPlaying(false);
            };
          };
        }
      } catch (err: any) {
        if (err.name === 'AbortError') {
          console.log('❌ Fetch aborted');
        } else {
          console.error("TTS process error:", err);
        }
      }
      setIsResponding(false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        console.error('Error sending audio message:', error);
      }
      setIsResponding(false);
      setMessages(messages => {
        const newMessages = messages.filter(msg => msg.content !== '');
        return [
          ...newMessages,
          {
            role: 'system',
            content: uiText.errorResponse,
            isUser: false,
            timestamp: new Date()
          }
        ];
      });
    }
  };
  const handleStopResponse = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsResponding(false);
    isRespondingRef.current = false;

    setMessages((prevMessages) => {
      const newMessages = [...prevMessages];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && !lastMessage.isUser && !lastMessage.content) {
        newMessages[newMessages.length - 1] = {
          ...lastMessage,
          content: 'रोक दिया गया।',
        };
      }
      return newMessages;
    });
  };

  const handleSendMessage = async (
    e: React.FormEvent | React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    e.preventDefault();

    // Strict synchronous check using ref
    if (isRespondingRef.current) {
      console.log('Duplicate submission blocked by ref');
      return;
    }

    if (!inputMessage.trim() && pendingMedia.length === 0) {
      return;
    }

    if (isMediaProcessing) {
      setMediaError('Please wait, media is still being compressed.');
      return;
    }

    // Immediately lock
    isRespondingRef.current = true;
    setIsResponding(true);

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found. Please login.');
      setIsResponding(false);
      isRespondingRef.current = false;
      return;
    }

    if (await refreshCreditUsage()) {
      setIsCreditModalOpen(true);
      setIsResponding(false);
      isRespondingRef.current = false;
      return;
    }

    setIsResponding(true);

    try {
      console.log("message getting..........")
      // Create FormData
      const formData = new FormData();
      if (isExamMode) {
        formData.append('question', inputMessage);
        formData.append('subject', selectedSubject);
        formData.append('language', selectedLanguage);
        formData.append('audioFlag', 'false'); // Use audioFlag for backend
        formData.append('examName', selectedExamName);
        formData.append('topic', incompleteTopic);
        formData.append('subtopic', incompleteSubtopic);
        formData.append('courseId', selectedDocumentId);
        formData.append('examMode', 'true');
      }
      else {
        formData.append('question', inputMessage);
        formData.append('subject', sessionSubject);
        formData.append('language', selectedLanguage);
        formData.append('sessionId', sessionId || '');
        formData.append('audioFlag', 'false'); // Use audioFlag for backend
        formData.append('examMode', 'false');
        if (isZapTalkMode) {
          formData.append('topic', zaptalkTopic);
          formData.append('chatMode', 'zaptalk');
        }
      }
      const imageMedia = pendingMedia.filter((item): item is PendingMedia & { mediaType: 'image' } => (
        item.mediaType === 'image'
      ));

      imageMedia.forEach((item) => {
        formData.append('images', item.file);
      });

      // Don't add user message to UI if it's "Hi" in exam mode
      if (!(isExamMode && isAutoGreetingMessage(inputMessage))) {
        const userImages: ImagePreview[] = imageMedia.reduce<ImagePreview[]>((accumulator, item) => {
          if (!item.preview) {
            return accumulator;
          }

          accumulator.push({
            id: item.id,
            file: item.file,
            preview: item.preview,
          });
          return accumulator;
        }, []);

        // Add user message with all images
        const userMessage: Message = {
          role: 'user',
          content: inputMessage,
          isUser: true,
          timestamp: new Date(),
          images: userImages.length ? userImages : undefined
        };
        setMessages(messages => [...messages, userMessage]);
      }

      setInputMessage('');
      setPendingMedia([]);
      setMediaError(null);

      // Add loading message
      const loadingMessage: Message = {
        role: 'assistant',
        content: '',
        isUser: false,
        timestamp: new Date()
      };
      setMessages(messages => [...messages, loadingMessage]);

      // Illustrate the answer with Wikipedia/Commons images, fetched in parallel
      // (never blocks the streamed answer). Not for ZapTalk or exam-mode greetings.
      if (!isZapTalkMode && !(isExamMode && isAutoGreetingMessage(inputMessage))) {
        void fetchAndAttachTopicImages(
          inputMessage,
          'chat-topic',
          selectedSubject || 'General',
          sessionId,
        );
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}${llmGenerateEndpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
        signal: getAbortSignal(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const ragImageUrls = getRagImageUrlsFromResponse(response);
      console.log("IMAGE....", ragImageUrls);
      attachImagesToLatestAssistantMessage(ragImageUrls, 'chat-rag');

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response reader available');
      }

      let accumulatedContent = '';
      const decoder = new TextDecoder('utf-8');
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          const finalChunk = decoder.decode();
          if (finalChunk) {
            accumulatedContent += finalChunk;
          }
          applyBufferedAssistantContent(accumulatedContent, { final: true });
          break;
        }

        // Convert the chunk to text
        const chunk = decoder.decode(value, { stream: true });
        accumulatedContent += chunk;

        applyBufferedAssistantContent(accumulatedContent);

        // Small delay to make streaming smoother
        await new Promise(resolve => setTimeout(resolve, 10));
      }

    } catch (error: any) {
      // Ignore abort errors (user stopped response)
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted by user');
        return;
      }

      console.error('Error in chat:', error);

      const errorMessage: Message = {
        role: 'system',
        content: uiText.errorResponse,
        isUser: false,
        timestamp: new Date()
      };
      setMessages(messages => {
        // Remove the loading message if it exists
        const newMessages = messages.filter(msg => msg.content !== '');
        return [...newMessages, errorMessage];
      });
    } finally {
      setIsResponding(false);
      isRespondingRef.current = false;
    }
  };

  // Add cleanup for abort controller
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const toggleTheme = () => {
    toggleMode();
  };

  const toggleExamMode = () => {
    setIsExamModeOpen(!isExamModeOpen);

    // If opening exam mode and we have current exam result, check for incomplete topics
    if (!isExamModeOpen && examResult?.subject && examResult?.examName) {
      findIncompleteTopics(examResult.subject, examResult.examName);
    }
  };

  // Fetch user's saved exam topics list
  const fetchMyExamTopics = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login first.');
      return;
    }
    try {
      setIsMyTopicsOpen(true);
      setIsLoadingMyTopics(true);
      setMyTopicsError(null);
      const url = `${process.env.REACT_APP_API_URL}/api/exam-topics/mine?subject=${encodeURIComponent(selectedSubject)}&limit=20&page=1`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: getAbortSignal(),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed with status ${res.status}`);
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setMyTopicsList(list);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        setMyTopicsError(error?.message || 'Failed to load your exam topics');
      }
    } finally {
      setIsLoadingMyTopics(false);
    }
  };

  // Open a saved exam topics document and render via topics tree
  const openSavedTopics = (doc: any) => {
    console.log('📄 Document Selected!');
    console.log('🆔 Document ID:', doc?._id);
    console.log('📋 Document Name:', doc?.examName || doc?.examType);
    console.log('📚 Subject:', doc?.subject);
    console.log('📊 Full Document:', doc);

    // Clear current messages when switching to a new exam
    resetMessagesState();
    console.log('🧹 Messages cleared for new exam');

    // Store the selected document ID
    setSelectedDocumentId(doc?._id || '');
    console.log('💾 Selected Document ID stored in state:', doc?._id);

    // Store the exam name and subject for API calls
    const examNameValue = doc?.examName || doc?.examType || '';
    setSelectedExamName(examNameValue);
    setSelectedSubject(doc?.subject || selectedSubject || 'General');
    console.log('📝 Exam Name stored in state:', examNameValue);

    const payload = doc?.topics ? doc : (doc?.data?.topics ? doc.data : doc);
    setExamResult(payload);
    setExamResultTitle(doc?.examType || doc?.examName || 'Exam Topics');
    setIsFromSavedTopics(true);
    setIsExamResultOpen(true);

    // Get incomplete topics when opening saved document
    getNextIncompleteTopic(doc);
  };

  const handleExamTypeChange = (value: string) => {
    if (value === 'Other') {
      setShowCustomExamInput(true);
      setSelectedExamType('Other');
    } else {
      setShowCustomExamInput(false);
      setSelectedExamType(value);
      setCustomExamType('');
    }
  };
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [customExamError, setCustomExamError] = useState<string | null>(null);

  const validateCustomExamType = (value: string) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setCustomExamError('Custom exam type cannot be empty.');
      return false;
    }
    const exists = examOptions.some(
      (option) => option.toLowerCase() === trimmedValue.toLowerCase()
    );
    if (exists) {
      setCustomExamError('This exam type already exists in the list.');
      return false;
    }
    setCustomExamError(null);
    return true;
  };

  const handleCustomExamTypeChange = (value: string) => {
    setCustomExamType(value);
    validateCustomExamType(value);
  };

  const handleScoreValueChange = (value: string) => {
    if (value === '') {
      setScoreValue('');
      setScoreError(null);
      return;
    }

    const numValue = parseFloat(value);

    if (scoreType === 'percentage') {
      if (isNaN(numValue) || numValue < 1 || numValue > 100) {
        setScoreError('Percentage must be between 1 and 100.');
      } else {
        setScoreValue(value);
        setScoreError(null);
      }
    } else if (scoreType === 'score') {
      if (isNaN(numValue) || numValue < 1 || numValue > 5) {
        setScoreError('Score must be between 1 and 5.');
      } else {
        setScoreValue(value);
        setScoreError(null);
      }
    }
  };

  const groupedSessions = useMemo(() => groupSessionsByDate(), [chatSessions]);
  const selectedSubjectLabel = isZapTalkMode
    ? (selectedZapTalkTopic ? `ZapTalk - ${selectedZapTalkTopic}` : 'ZapTalk')
    : (selectedSubject || 'General');

  const handleDebugSubjectSelect = (subject: string) => {
    setSelectedSubject(subject);
  };

  const handleRefreshIncompleteTopics = async () => {
    await findIncompleteTopics(getCurrentSubject(), getCurrentExamName());
  };

  const handleResumeCourse = async () => {
    console.log('🚀 Resume Course button clicked!');
    console.log('📋 Current examResultTitle:', examResultTitle);
    console.log('📊 Current examResult:', examResult);
    console.log('🎯 Current incompleteTopic:', incompleteTopic);
    console.log('📖 Current incompleteSubtopic:', incompleteSubtopic);
    console.log('🔍 BEFORE: isExamMode =', isExamMode);
    console.log('🔍 BEFORE: Sessions should be visible =', !isExamMode);

    resetMessagesState();
    setIsMyTopicsOpen(false);
    setIsExamResultOpen(false);

    console.log('✅ State updated - modals closed');

    if (examResult?.subject && examResult?.examName) {
      console.log('🔍 Fetching incomplete topics for:', examResult.subject, examResult.examName);
      await findIncompleteTopics(examResult.subject, examResult.examName);
    } else {
      console.log('⚠️ No subject or examName found in examResult');
    }

    console.log('💬 Loading previous exam mode chats...');
    await fetchExamModeChats();

    setTimeout(async () => {
      if (messages.length === 0) {
        console.log('📝 No previous chats found, sending Hi message...');
        setInputMessage(initialGreeting);
        setTimeout(async () => {
          const fakeEvent = { preventDefault: () => { } } as React.FormEvent;
          await handleSendMessage(fakeEvent);
        }, 100);
      } else {
        console.log('👋 Previous chats found, showing welcome message...');
        const welcomeMessage: Message = {
          role: 'assistant',
          content: uiText.welcome,
          isUser: false,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, welcomeMessage]);
      }
    }, 1000);
  };
  const handleStartExamSubmit = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login first.');
      return;
    }

    const examName = (selectedExamType === 'Other' ? customExamType : selectedExamType)?.trim();
    if (!examName) {
      alert('Please select or enter an exam type.');
      return;
    }

    if (selectedExamType === 'Other' && !validateCustomExamType(customExamType)) {
      return;
    }

    if (!scoreValue || Number(scoreValue) <= 0) {
      alert('Please enter a valid target number.');
      return;
    }

    try {
      setIsExamSubmitting(true);
      setExamSubmitError(null);

      const url = `${process.env.REACT_APP_API_URL}/api/exam-topics/generate`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          examName: examName,
          subject: selectedSubject,
          timeAvailable: Number(examDays),
          expectedTarget: Number(scoreValue)
        }),
        signal: getAbortSignal(),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed with status ${res.status}`);
      }

      const data = await res.json();
      const payload = unwrapApiPayload(data);
      const normalizedExamName = payload?.examName || data?.examName || examName;
      const normalizedSubject = payload?.subject || data?.subject || selectedSubject || 'General';
      const normalizedCourseId = await resolveExamDocumentId(
        [
          payload?._id,
          payload?.examId,
          payload?.sessionId,
          payload?.courseId,
          data?._id,
          data?.examId,
          data?.sessionId,
          data?.courseId,
        ],
        normalizedSubject,
        normalizedExamName
      );
      const normalizedExamResult = {
        ...payload,
        sessionId: normalizedCourseId || payload?.sessionId || data?.sessionId,
        _id: normalizedCourseId || payload?._id || data?._id,
        createdAt: payload?.createdAt || data?.createdAt || new Date().toISOString(),
        examName: normalizedExamName,
        subject: normalizedSubject,
      };

      setExamResult(normalizedExamResult);
      setSelectedExamName(normalizedExamName);
      setSelectedSubject(normalizedSubject);
      setSelectedDocumentId(normalizedCourseId || '');
      if (!normalizedCourseId) {
        setExamTopicsError('Exam generated, but topic sidebar is syncing. Please retry in a moment.');
      } else {
        setExamTopicsError(null);
      }

      if (Array.isArray(normalizedExamResult.topics) && normalizedExamResult.topics.length > 0) {
        const firstTopic = normalizedExamResult.topics[0];
        const firstSubtopic = firstTopic?.subtopics?.[0];
        const firstSubtopicName = typeof firstSubtopic === 'string'
          ? firstSubtopic
          : (firstSubtopic?.name || '');
        setIncompleteTopic(firstTopic?.topic || '');
        setIncompleteSubtopic(firstSubtopicName);
      }

      setIsFromSavedTopics(false);
      setIsExamResultOpen(true);
      setIsExamModeOpen(false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        const message = error?.response?.data?.message || error?.message || 'Failed to generate exam topics';
        setExamSubmitError(message);
      }
    } finally {
      setIsExamSubmitting(false);
    }
  };

  const handleNewChat = () => {
    if (isZapTalkMode) {
      const matchedOption = ZAPTALK_SUPPORT_OPTIONS.find(
        (option) => option.topic.toLowerCase() === selectedZapTalkTopic.toLowerCase(),
      );
      setSelectedZapTalkCategory(matchedOption?.topic || '');
      setIsZapTalkCategoryModalOpen(true);
      return;
    }

    resetMessagesState();
    createNewSession();
  };

  const handleZapTalkCategoryContinue = () => {
    if (!selectedZapTalkCategory) {
      return;
    }
    void resolveZapTalkSessionByTopic(selectedZapTalkCategory);
  };

  const handleZapTalkCategorySkip = () => {
    void resolveZapTalkSessionByTopic(ZAPTALK_DEFAULT_TOPIC);
  };

  function groupSessionsByDate() {
    const groups: { [key: string]: ChatSession[] } = {
      'Today': [],
      'Yesterday': [],
      'Previous 7 Days': [],
      'Previous 30 Days': []
    };

    const now = new Date();
    chatSessions.forEach(session => {
      const diffDays = Math.floor((now.getTime() - session.createdAt.getTime()) / (1000 * 60 * 60 * 24));

      if (diffDays === 0) groups['Today'].push(session);
      else if (diffDays === 1) groups['Yesterday'].push(session);
      else if (diffDays <= 7) groups['Previous 7 Days'].push(session);
      else if (diffDays <= 30) groups['Previous 30 Days'].push(session);
    });

    return groups;
  }

  const handleMicClick = async () => {
    if (!isVoiceSupported) {
      alert('Voice responses are currently available only in English.');
      return;
    }
    if (isRecording) {
      stopRecording();
    } else {
      await startRecording();
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.querySelector('.sidebar');
      const menuButton = document.querySelector('.mobile-menu-toggle');

      if (sidebar &&
        !sidebar.contains(event.target as Node) &&
        menuButton &&
        !menuButton.contains(event.target as Node)) {
        setIsSidebarOpen(false);
      }
    };

    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarOpen]);

  // Close voice menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const voiceContainer = document.querySelector('.voice-selector-container');

      if (voiceContainer && !voiceContainer.contains(event.target as Node)) {
        setIsVoiceMenuOpen(false);
      }
    };

    if (isVoiceMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isVoiceMenuOpen]);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const languageContainer = document.querySelector('.language-selector-container');

      if (languageContainer && !languageContainer.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };

    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLanguageMenuOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    // Reset height to auto first
    e.target.style.height = 'auto';

    // Calculate new height only if there's content
    if (e.target.value) {
      const computed = window.getComputedStyle(e.target);
      const height = parseInt(computed.getPropertyValue('border-top-width'), 10)
        + parseInt(computed.getPropertyValue('padding-top'), 10)
        + e.target.scrollHeight
        + parseInt(computed.getPropertyValue('padding-bottom'), 10)
        + parseInt(computed.getPropertyValue('border-bottom-width'), 10);

      e.target.style.height = `${Math.min(height, 120)}px`;
    } else {
      // Reset to minimum height when empty
      e.target.style.height = '40px';
    }
  };

  const submitMessage = async () => {
    console.log('Submit message called with state:', {
      inputMessage,
      sessionId,
      isSubmitting,
      hasToken: !!localStorage.getItem('token'),
      sessionCreated: sessionCreated.current,
      messageLength: inputMessage?.trim().length
    });

    if (!localStorage.getItem('token')) {
      console.error('No token found, cannot submit message');
      return;
    }

    if (!sessionId) {
      console.error('No sessionId available, cannot submit message');
      return;
    }

    if (isSubmitting || (!inputMessage?.trim() && pendingMedia.length === 0) || isMediaProcessing) {
      console.log('Message submission blocked:', {
        isSubmitting,
        hasInput: !!inputMessage?.trim(),
        pendingMediaCount: pendingMedia.length,
        isMediaProcessing,
        hasSessionId: !!sessionId
      });
      return;
    }

    try {
      const creditsDepleted = await refreshCreditUsage();
      if (creditsDepleted) {
        setIsCreditModalOpen(true);
        return;
      }

      setIsSubmitting(true);
      console.log('Starting message submission...');
      const fakeEvent = { preventDefault: () => { } } as React.FormEvent;
      await handleSendMessage(fakeEvent);
      console.log('Message submitted successfully');
      await refreshCreditUsage();
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('❌ Fetch aborted');
      } else {
        console.error('Error submitting message:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMessage();
  };

  // Function to process math in text
  // Function to render message content with markdown and images


  // Add function to remove specific pending file
  const handleRemoveMedia = (id: string) => {
    setPendingMedia(prev => prev.filter(item => item.id !== id));
  };

  const startRecording = async () => {
    try {
      if (isCreditExhausted) {
        setIsCreditModalOpen(true);
        return;
      }
      console.log("Attempting to start recording...");
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log("Microphone access granted.");

      const options = { mimeType: 'audio/webm;codecs=opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.warn(`${options.mimeType} is not supported, using default.`);
        mediaRecorderRef.current = new MediaRecorder(stream);
      } else {
        mediaRecorderRef.current = new MediaRecorder(stream, options);
        console.log("MediaRecorder created with options:", options);
      }

      const mediaRecorder = mediaRecorderRef.current;

      mediaRecorder.addEventListener("dataavailable", (event) => {
        console.log(`dataavailable event fired. Data size: ${event.data.size}`);
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      });

      mediaRecorder.start();
      console.log("Recording started.");
      setIsRecording(true);

      let time = 0;
      timerRef.current = setInterval(() => {
        time += 1;
        setRecordingTime(time);
      }, 1000);

    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Could not access microphone. Please check browser permissions and ensure you are on a secure (HTTPS) connection.");
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      console.log("Stopping recording...");
      // Make sure to keep the recorded time
      const finalRecordingTime = recordingTime;

      // Clear the timer first
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Add the stop event listener before stopping
      mediaRecorderRef.current.addEventListener("stop", () => {
        console.log("Recording stopped event fired.");
        console.log("Collected chunks:", chunksRef.current);

        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm;codecs=opus" });
        const audioFile = new File([audioBlob], 'audio.webm', { type: 'audio/webm' });
        console.log(`Audio blob created. Size: ${audioBlob.size}, Type: ${audioBlob.type}`);

        if (audioBlob.size === 0) {
          console.error("Recorded audio is empty. Nothing to play.");
          setRecordingTime(0);
          setIsRecording(false);
          const stream = mediaRecorderRef.current?.stream;
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
          return;
        }

        handleSendAudioMessage(audioBlob, audioFile, finalRecordingTime);
        setRecordingTime(0);
        setIsRecording(false);

        // Stop all tracks from the stream
        const stream = mediaRecorderRef.current?.stream;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }, { once: true });

      // Stop recording after setting up the event listener
      mediaRecorderRef.current.stop();
    }
  };

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  if (showZapTalkSplash) {
    return (
      <div className="zaptalk-splash-screen" role="status" aria-live="polite" aria-label="ZapTalk splash screen">
        <div className="zaptalk-splash-content">
          <h1>ZapTalk</h1>
          <p>Mental Health Counselor</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-container ${isDarkMode ? 'dark' : 'light'} ${isZapTalkMode ? 'zaptalk-mode' : ''}`.trim()}>
      <ChatSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onNewChat={handleNewChat}
        isExamMode={isExamMode}
        isDateFilterOpen={isDateFilterOpen}
        onToggleDateFilter={() => setIsDateFilterOpen(!isDateFilterOpen)}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApplyDateFilter={handleDateFilter}
        onClearDateFilter={clearDateFilter}
        isLoadingSessions={isLoadingSessions}
        sessionsError={sessionsError}
        onRetrySessions={() => fetchLatestSessions(1)}
        latestSessions={latestSessions}
        selectedSessionId={selectedSessionId}
        onSelectSession={handleSessionSelect}
        currentPage={currentPage}
        totalPages={totalPages}
        totalSessions={totalSessions}
        hasPrevPage={hasPrevPage}
        hasNextPage={hasNextPage}
        onPrevPage={handlePrevPage}
        onNextPage={handleNextPage}
        onPageChange={handlePageChange}
        groupedSessions={groupedSessions}
        onSubjectDebugSelect={handleDebugSubjectSelect}
        selectedSubject={sessionSubject}
        selectedDocumentId={selectedDocumentId}
        selectedExamName={selectedExamName}
        sessionId={sessionId}
        isZapTalkMode={isZapTalkMode}
        examSidebarMode={examSidebarMode}
        onExamSidebarModeChange={handleExamSidebarModeChange}
        examTopics={examSidebarTopics}
        examNotesTreeNodes={examNotesTreeNodes}
        selectedExamTopicNodeId={selectedExamTopicNodeId}
        lockedExamNodeIds={lockedExamNodeIds}
        selectedNotesTopicId={selectedNotesTopicId}
        noteCountsByTopic={noteCountsByTopic}
        isLoadingExamTopics={isLoadingExamTopics}
        examTopicsError={examTopicsError}
        onRetryExamTopics={() => {
          void loadExamTopicsForSidebar();
        }}
        onSelectExamTopic={handleExamTopicSelect}
        onSelectNotesTopic={handleNotesTopicSelect}
        selectedVoice={selectedVoice}
        isBotSpeaking={isAudioPlaying}
      />

      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}

      <main className="chat-content">
        <ChatHeader
          isSidebarOpen={isSidebarOpen}
          onToggleSidebar={toggleSidebar}
          selectedSubject={selectedSubjectLabel}
          isZapTalkMode={isZapTalkMode}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
          selectedVoice={selectedVoice}
          onSelectVoice={setSelectedVoice}
          voiceMenuOpen={isVoiceMenuOpen}
          onVoiceMenuToggle={setIsVoiceMenuOpen}
          availableVoices={availableVoices}
          currentVoice={getCurrentVoiceDetails()}
          selectedLanguage={selectedLanguage}
          onSelectLanguage={setSelectedLanguage}
          languageMenuOpen={isLanguageMenuOpen}
          onLanguageMenuToggle={setIsLanguageMenuOpen}
          availableLanguages={availableLanguages}
          zaptalkTopic={zaptalkTopic}
        />

        {isNotesModeActive ? (
          <ExamNotesPanel
            hasTopics={examNotesTreeNodes.length > 0}
            selectedTopicTitle={selectedNotesPanelTitle}
            notes={selectedTopicNotes}
            isLoadingNotes={isLoadingSelectedTopicNotes}
            notesError={notesError}
            noteDraft={newNoteDraft}
            noteDraftError={newNoteError}
            onNoteDraftChange={handleNewNoteDraftChange}
            onSaveNote={handleSaveNote}
            isSavingNote={isSavingNote}
            editingNoteId={editingNoteId}
            editingNoteDraft={editingNoteDraft}
            editingNoteError={editingNoteError}
            onStartEdit={handleStartEditNote}
            onEditDraftChange={handleEditingNoteDraftChange}
            onCancelEdit={handleCancelEditingNote}
            onUpdateNote={handleUpdateNote}
            updatingNoteId={updatingNoteId}
            deletingNoteId={deletingNoteId}
            onDeleteNote={handleDeleteNote}
            maxLength={EXAM_NOTE_MAX_LENGTH}
          />
        ) : (
          <>
            <ChatMessages
              messages={messages}
              messagesEndRef={messagesEndRef}
              messagesContainerRef={messagesContainerRef}
              isLoadingSessions={isLoadingSessions}
              hasMoreMessages={hasMoreMessages}
              isLoadingOlderMessages={isLoadingOlderMessages}
              onLoadMoreMessages={handleLoadOlderMessages}
              selectedSubject={selectedSubjectLabel}
              isAudioPlaying={isAudioPlaying}
              setIsAudioPlaying={setIsAudioPlaying}
              botAudioRef={botAudioRef}
              isBotAudioManuallyPlaying={isBotAudioManuallyPlaying}
              setIsBotAudioManuallyPlaying={setIsBotAudioManuallyPlaying}
              isLastBotResponseComplete={isLastBotResponseComplete}
              setIsLastBotResponseComplete={setIsLastBotResponseComplete}
            />

            <ChatInput
              inputMessage={inputMessage}
              onInputChange={handleInputChange}
              onPaste={handlePaste}
              onSendMessage={handleSendMessage}
              isResponding={isResponding}
              isRecording={isRecording}
              recordingTime={recordingTime}
              isAudioPlaying={isAudioPlaying}
              onMicClick={handleMicClick}
              pendingMedia={pendingMedia}
              onRemoveMedia={handleRemoveMedia}
              onSelectMedia={handleMediaSelection}
              isMediaProcessing={isMediaProcessing}
              mediaError={mediaError}
              onCallClick={() => navigate('/vad')}
              disabled={isCreditExhausted || (isZapTalkMode && !selectedZapTalkTopic)}
              onStopResponse={handleStopResponse}
              isVoiceSupported={isVoiceSupported}
              voiceDisabledReason="Voice responses are available only in English."
            />

            {isCreditExhausted && (
              <div className="credit-limit-banner">
                <span className="credit-limit-banner__text">
                  You have used all free AI credits. Subscribe to continue chatting.
                </span>
                <button
                  type="button"
                  className="credit-limit-banner__cta"
                  onClick={() => navigate('/pricing')}
                >
                  Subscribe
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {isZapTalkCategoryModalOpen && (
        <div
          className="zaptalk-concern-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="zaptalk-concern-title"
        >
          <div className="zaptalk-concern-modal">
            <h3 id="zaptalk-concern-title">Choose your ZapTalk support topic</h3>
            <p>
              Select one option to continue with the topic that best matches your current need.
            </p>
            <div className="zaptalk-concern-grid">
              {ZAPTALK_SUPPORT_OPTIONS.map((option) => {
                const isSelected = selectedZapTalkCategory === option.topic;
                return (
                  <button
                    key={option.topic}
                    type="button"
                    className={`zaptalk-concern-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedZapTalkCategory(option.topic)}
                  >
                    <div className="zaptalk-concern-option-header">
                      <span className="zaptalk-concern-topic">{option.topic}</span>
                      <span className={`zaptalk-concern-check ${isSelected ? 'selected' : ''}`}>
                        {isSelected ? 'Selected' : 'Select'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="zaptalk-concern-actions">
              <button
                type="button"
                className="zaptalk-concern-skip"
                onClick={handleZapTalkCategorySkip}
              >
                Skip
              </button>
              <button
                type="button"
                className="zaptalk-concern-continue"
                onClick={handleZapTalkCategoryContinue}
                disabled={!selectedZapTalkCategory}
              >
                Continue to ZapTalk
              </button>
            </div>
          </div>
        </div>
      )}

      <ExamModeDialog
        isOpen={isExamModeOpen}
        onClose={toggleExamMode}
        examDays={examDays}
        onDaysChange={setExamDays}
        examOptions={examOptions}
        gradeOptions={gradeOptions}
        selectedExamType={selectedExamType}
        onExamTypeChange={handleExamTypeChange}
        showCustomExamInput={showCustomExamInput}
        customExamType={customExamType}
        onCustomExamTypeChange={handleCustomExamTypeChange}
        customExamError={customExamError}
        expectedGrade={expectedGrade}
        onExpectedGradeChange={setExpectedGrade}
        scoreType={scoreType}
        onScoreTypeChange={setScoreType}
        scoreValue={scoreValue}
        onScoreValueChange={handleScoreValueChange}
        scoreError={scoreError}
        onSubmit={handleStartExamSubmit}
        isSubmitting={isExamSubmitting}
        submitError={examSubmitError}
      />
      <MyTopicsModal
        isOpen={isMyTopicsOpen}
        onClose={() => setIsMyTopicsOpen(false)}
        isLoading={isLoadingMyTopics}
        error={myTopicsError}
        topics={myTopicsList}
        onOpenTopic={openSavedTopics}
      />

      {isCreditModalOpen && (
        <div className="credit-modal-overlay">
          <div className="credit-modal">
            <h3>Free credits exhausted</h3>
            <p>You&apos;ve used all of your complimentary AI credits. Please subscribe to continue.</p>
            <div className="credit-modal-actions">
              <button type="button" onClick={() => setIsCreditModalOpen(false)}>
                Dismiss
              </button>
              <button
                type="button"
                className="cta"
                onClick={() => {
                  setIsCreditModalOpen(false);
                  navigate('/pricing');
                }}
              >
                View plans
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
